import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { HazardCategory, SiteApprovalLineMetaData, ApprovalLineMetaRow, OrganizationCategory, ReportTemplate, CatalogEntry, ReportComposerPreviewResponse, ReportComposerPreviewContext, ImprovementCandidatePart, ImprovementCandidatesResponse, ComposerWidget, ApprovalLineDefinitionRow, ApprovalSignatureMode, SiteStatus, CompanyTrustLevel, OrgWithdrawalAction, WithdrawalReasonType, SplitPlan, SiteAssignmentType, UserFeedbackType, UserFeedbackAttachmentType, AdminLawAnnexesResponse, AdminRuleListResponse, AdminRuleDetailResponse } from '@safetyhub/shared';
import type { AdminApprovalSignature } from './composerApprovalData';
import type { AdminPermissionKey } from './adminPermissionKeys';
import type { SiteEditableStatus } from './sites';
// ⚠️ **반드시 `import type`으로 유지할 것** — `ml-sources.ts`는 이 파일의 axios 인스턴스를
// 기본 import하므로, 값(라벨 맵·`mlSourcesApi`)을 여기로 들여오면 런타임 import 사이클이 된다.
// 타입은 `isolatedModules: true`에서 완전히 지워지므로 사이클이 생기지 않는다.
import type { MlPhotoGrade, PhotoPurpose } from './ml-sources';
import { useAuthStore } from '../store/auth';

// API 버전 (버전 변경 시 이 값만 수정)
const API_VERSION = 'v1';

// API 기본 URL
const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const API_BASE = `${API_HOST}/api/${API_VERSION}`;

/**
 * 서버가 내려주는 파일 경로(report.fileUrl 등)를 브라우저가 열 수 있는 절대 URL로 만든다.
 * 서버는 상대경로(`/uploads/...`)를 내려주는데 admin은 API와 다른 origin에서 서빙되므로
 * 그대로 href에 넣으면 admin origin을 가리켜 404가 난다(web print-preview와 동일 처리).
 */
export function toAbsoluteApiFileUrl(url: string): string {
  return url.startsWith('http') ? url : `${API_HOST}${url}`;
}

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: Authorization 헤더 자동 추가
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 네트워크/서버 에러 재시도 설정
const NETWORK_RETRY_BASE_DELAY = 1000; // 기본 1초
const NETWORK_MAX_RETRIES = 10; // 최대 10회 재시도
const NETWORK_RETRY_MAX_DELAY = 8000; // 최대 8초 간격

// Idempotent HTTP 메서드 (재시도 안전)
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

/** 지수 backoff 대기: min(base * 2^retryCount, maxDelay) */
const getRetryDelay = (retryCount: number): number =>
  Math.min(NETWORK_RETRY_BASE_DELAY * Math.pow(2, retryCount), NETWORK_RETRY_MAX_DELAY);

// 응답 인터셉터: 토큰 만료 시 자동 갱신
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _networkRetryCount?: number;
    };

    // 네트워크 에러(서버 다운) 또는 5xx 에러인 경우 자동 재시도
    // POST/PATCH는 중복 생성 위험으로 기본 제외. Idempotent 메서드(GET/HEAD/OPTIONS/PUT/DELETE)만 재시도
    const isNetworkError = !error.response || error.code === 'ERR_NETWORK';
    const isServerError =
      error.response?.status !== undefined &&
      error.response.status >= 500 &&
      error.response.status < 600;
    const isRetryable = isNetworkError || isServerError;
    const method = (originalRequest?.method || '').toUpperCase();
    const isIdempotent = IDEMPOTENT_METHODS.has(method);

    if (isRetryable && isIdempotent && originalRequest) {
      const retryCount = originalRequest._networkRetryCount || 0;
      if (retryCount < NETWORK_MAX_RETRIES) {
        originalRequest._networkRetryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);
        if (retryCount === 0) {
          console.log(
            `[admin/api.ts] ${isServerError ? `HTTP ${error.response?.status}` : 'Network error'}, retrying (${retryCount + 1}/${NETWORK_MAX_RETRIES}) in ${delay}ms...`
          );
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(originalRequest);
      }
      console.log('[admin/api.ts] Retryable error, max retries exceeded');
    }

    // 401 에러이고, 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 로그인 요청은 토큰 갱신 시도하지 않음
      if (originalRequest.url?.includes('/admin/auth/login')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // 이미 갱신 중이면 큐에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Admin 전용 refresh 엔드포인트 사용
        const response = await axios.post(`${API_BASE}/admin/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        useAuthStore.getState().setTokens(accessToken, newRefreshToken);

        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// External Services 타입
export interface SmsPrefix {
  id: string;
  prefix: string;
  label: string;
  serviceKey: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface SmsDailyStatsItem {
  date: string;
  smsCount: number;
  lmsCount: number;
  mmsCount: number;
  totalCount: number;
  successCount: number;
  failCount: number;
}

export interface SmsSendLogItem {
  mid: number;
  type: string;
  receiver: string;
  message: string;
  sentAt: string;
  smsState?: string;
  sendDate?: string | null;
}

// API 응답 타입
interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: { code: string; message: string } | null;
  meta?: {
    total: number;
    limit: number;
    /** 페이지네이션 목록만 준다 — 상한형 검색(이관 대상 후보 등)은 페이지가 없다 */
    offset?: number;
    /** 상한형 검색: 결과가 상한에 걸려 잘렸는가 */
    truncated?: boolean;
    /** 상한형 검색: 이 길이 미만이면 서버가 조회 자체를 하지 않는다 */
    minQueryLength?: number;
    /**
     * 사용자 목록(`getUsers`)만 준다 — 상태 필터를 뺀 같은 조건(조직·검색)의 상태별 건수.
     * 한 페이지가 아니라 전체 분모다.
     */
    counts?: { all: number; active: number; withdrawn: number };
  };
}

// Axios를 사용하는 API 헬퍼 함수
async function fetchApi<T>(
  endpoint: string,
  options?: {
    method?: string;
    body?: string | FormData;
    headers?: Record<string, string>;
  }
): Promise<ApiResponse<T>> {
  const isFormData = options?.body instanceof FormData;
  let data: unknown;
  if (isFormData) {
    data = options.body;
  } else if (typeof options?.body === 'string') {
    data = JSON.parse(options.body);
  }
  const response = await api.request<ApiResponse<T>>({
    url: endpoint,
    method: options?.method || 'GET',
    data,
    headers: isFormData ? { 'Content-Type': undefined } : options?.headers,
  });
  return response.data;
}

// Auth API - Admin 전용 인증 엔드포인트
export const authApi = {
  /**
   * 슈퍼관리자 로그인
   * - /api/v1/admin/auth/login 사용
   * - 백엔드에서 isSuperAdmin 검증 수행
   */
  login: (identifier: string, password: string) =>
    fetchApi<{
      id: string;
      email: string;
      name: string;
      tokens: { accessToken: string; refreshToken: string };
    }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    }),

  /**
   * 토큰 갱신
   */
  refresh: (refreshToken: string) =>
    fetchApi<{
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>('/admin/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  /**
   * 비밀번호 재설정 요청 (이메일 링크만 발송)
   */
  requestPasswordReset: (email: string) =>
    fetchApi<{
      message: string;
      expiresAt: string;
    }>('/admin/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * 비밀번호 재설정 - SMS 인증코드 요청
   */
  requestResetSmsCode: (token: string) =>
    fetchApi<{
      message: string;
      expiresAt: string;
      phoneMasked: string;
    }>('/admin/auth/password-reset/sms', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /**
   * 비밀번호 재설정 완료 (이중 인증 검증)
   */
  verifyPasswordReset: (token: string, smsCode: string, newPassword: string) =>
    fetchApi<{
      message: string;
      accountRestored?: boolean;
    }>('/admin/auth/password-reset/verify', {
      method: 'POST',
      body: JSON.stringify({ token, smsCode, newPassword }),
    }),
};

// 업종 트리 노드 — 서버 `AdminIndustryService.findTree`의 select와 같은 칸이다.
/** 현장 응답의 업종 분류(KSIC) — 서버 `SITE_INDUSTRY_INCLUDE`와 같은 형상 */
export interface AdminSiteIndustry {
  id: string;
  code: string;
  name: string;
  level: number;
}

export interface AdminIndustryTreeNode {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentId: string | null;
  level: number;
  isActive: boolean;
  _count: { children: number; hazardMappings: number };
  children: AdminIndustryTreeNode[];
}

/**
 * `GET /admin/regulations/annexes/unmapped-symbols` 응답 — 서버 `law/annex-grid.service.ts`의 `AnnexUnmappedSymbolsView` 사본.
 * 칸의 뜻(「현재」 run 규칙 · `byRestoreKey` 네 값)은 그 서버 docblock이 정본이다.
 */
export interface AnnexUnmappedSymbolsView {
  sourceDownloadEnabled: boolean;
  gridRulesVersion: number;
  restorerVersion: number;
  coverage: {
    annexes: number;
    byRestoreKey: { NA: number; OFF: number; CURRENT: number; OLDER: number };
    byRestoreStatus: Record<string, number>;
    lastRestoreAt: string | null;
    lastProcessedAt: string | null;
  };
  symbols: {
    font: string | null;
    code: string;
    count: number;
    annexes: { annexKey: string; ownerName: string; title: string; count: number }[];
  }[];
}

// 버전 정보 타입 (AppVersion과 일치)
export interface VersionInfo {
  name: string;
  version: string;
  buildNumber: number;
  fullVersion: string;
  commitHash: string;
  buildDate: string;
}

/**
 * 릴리즈 노트의 surface 값 — **서버 Prisma enum `ReleaseNotePlatform`의 손열거 사본**이다
 * (`apps/server/prisma/schema.prisma`가 정본이고 둘을 묶는 동기화 수단이 없다).
 *
 * admin 안에서는 **이 선언이 유일한 사본**이다 — 2026-10-15에 이 파일 세 자리 +
 * `pages/ReleaseNotes.tsx`의 `type Platform`, 넷을 여기로 접었다.
 *
 * 값이 일곱이고 성질이 셋으로 갈린다(결정
 * `2026-10-15-release-note-surfaces-as-a-set-and-popup-decisions.md` §A):
 * - **오늘의 surface**: `WEB` · `APP` · `ADMIN`
 * - **유산**: `ALL`(걷어낸 「전부」 태그) · `MOBILE`(`APP`의 선행 값) · `SERVER`(영향받는 surface에 귀속)
 * - **공집합**: `INTERNAL` — 공개 피드엔 안 뜨지만 **admin에서는 봐야 하는 값**이다(저작자가 그 축으로
 *   목록을 본다. 결정 `2026-10-15-internal-is-an-explicit-empty-surface-and-the-server-owns-its-enforcement.md`)
 *
 * ⚠️ 유산 세 값을 지우지 말 것 — 옛 행이 그 값을 여전히 들고 있고, 좁히면 그 행을 받는 자리가
 * 조용히 거짓이 된다. admin은 Vite(esbuild) 빌드라 **CI가 타입을 보지 않는다**(로컬 tsc로 닫을 것).
 */
export type ReleaseNotePlatform =
  | 'ALL'
  | 'WEB'
  | 'MOBILE'
  | 'APP'
  | 'ADMIN'
  | 'SERVER'
  | 'INTERNAL';

// ===== Report Composer 양식 분류(category) 타입 (2026-09-20 결정) =====
// 서버 ReportTemplateCategoryView(report-template-category.service.ts)에 대응. web 미사용이라 shared가
// 아니라 여기 둔다(결정 §6 — web이 쓰기로 정해지면 shared로 올린다). 「미분류」는 행이 아니라
// 양식의 categoryId = null. `Company.category`(기관 유형)와는 다른 모델 — 양식 분류다.
export interface ReportTemplateCategory {
  id: string;
  name: string;
  /** 관리 모달의 위/아래 이동 순서(오름차순). */
  sortOrder: number;
  /** 소속 양식 수 — 삭제 확인 「양식 N개가 미분류로 이동」의 N. */
  templateCount: number;
  createdAt: string;
  updatedAt: string;
}

/** 템플릿 응답에 동봉되는 분류 요약(id·name) — 선택기 그룹 헤더·속성 select 표시용. */
export interface ReportTemplateCategoryRef {
  id: string;
  name: string;
}

// ===== Report Composer 템플릿 타입 (3c-2) =====
// 서버 StoredReportTemplate(ReportTemplate & { id, enabled, categoryId, category, createdAt, updatedAt })에 대응.
// Date 필드는 JSON 직렬화로 ISO 문자열로 내려온다.
export interface StoredReportTemplate extends ReportTemplate {
  id: string;
  enabled: boolean;
  /** 양식 분류 id — null = 미분류. */
  categoryId: string | null;
  /** 동봉 분류 요약 — null = 미분류. */
  category: ReportTemplateCategoryRef | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 템플릿 생성 body — ReportTemplate 전체(키 포함) + optional enabled + optional categoryId
 * (신규 양식의 「분류」 select — null/미전달 모두 미분류).
 */
export type CreateReportTemplateBody = ReportTemplate & {
  enabled?: boolean;
  categoryId?: string | null;
};

/** 템플릿 부분 갱신 body — 전 필드 optional(key 변경=rename 지원). */
// ⚠️ Partial<ReportTemplate> & { policy?: … | null } 교차로 쓰면 프로퍼티 타입이 교집합돼
//    | null이 삼켜진다(TS 교차 타입 동작) — Omit으로 policy를 빼고 재선언해야 null이 살아남는다.
export type UpdateReportTemplateBody = Omit<
  Partial<ReportTemplate>,
  'policy' | 'importMeta' | 'titleTemplate' | 'defaultSimulationPresetId'
> & {
  // 기본 모사 프리셋 id — null=명시적 해제(DB NULL 클리어). titleTemplate과 같은 이유로
  // Omit 재선언(교차 타입은 | null이 삼켜짐 + undefined는 서버 skip이라 해제 미반영 함정).
  defaultSimulationPresetId?: string | null;
  enabled?: boolean;
  // 제목 규칙 — null=명시적 해제(DB NULL 클리어). policy와 같은 이유로 Omit 재선언
  // (교차 타입은 | null이 삼켜짐 + undefined는 서버 skip이라 해제 미반영 함정).
  titleTemplate?: string | null;
  // 노출·중복 정책 — null=명시적 해제(DB NULL 클리어). undefined(미전달)면 서버가 skip해
  // 해제가 영원히 미반영되는 함정(entryUserInput 2026-07-25 실사고 동형)이 있어 null 계약 필요.
  policy?: ReportTemplate['policy'] | null;
  // 양식 업로드 초안 메타 — 적용·무시로 repeatHints가 소진되면 null(명시적 해제)로 DB를 비운다.
  // policy와 같은 이유로 Omit 재선언(교차 타입은 | null이 삼켜짐).
  importMeta?: ReportTemplate['importMeta'] | null;
  // 양식 분류(category) — null=미분류로 해제(DB NULL), undefined=미변경(서버 skip). 저장 흐름이
  // 본문 body에 실어 함께 보낸다(별도 PATCH 없음, 2026-09-20 결정).
  categoryId?: string | null;
};

// ===== 양식 업로드(form-import) 타입 (2026-07-27 결정) =====
// 서버 form-import.service.ts의 FormImportProgress / transformer의 FormImportSummary와 1:1.
// 설계 정본: docs/decisions/2026-07-27-composer-form-import-ir.md

/** 반복 영역 제안(미적용) — 운영자가 에디터에서 확정한다. */
export interface FormImportRepeatHint {
  rowStart: number;
  rowEnd: number;
  reason: string;
}

/** 변환 요약 — 완료 화면에 그대로 노출. */
export interface FormImportSummary {
  fieldCount: number;
  tableCount: number;
  repeatHints: FormImportRepeatHint[];
  unsupported: string[];
}

/**
 * 성공한 초안 **대표 1건**. 배치의 정본 목록은 여기가 아니라 FormImportProgress.sheets다
 * (전건 실패·취소로 성공이 0건이면 result 자체가 없는데, 그때야말로 사유가 필요하다).
 */
export interface FormImportJobResult {
  templateKey: string;
  summary: FormImportSummary;
}

/** 배치 항목 1건(엑셀 시트 1장, 이미지/PDF 단건은 sheetName 없음)의 결과. */
export interface FormImportSheetOutcome {
  sheetName?: string;
  status: 'completed' | 'failed' | 'cancelled';
  templateKey?: string;
  summary?: FormImportSummary;
  error?: string;
}

/** 복수 시트 순차 배치의 진행 위치 — 항목 2개 이상일 때만 실린다. */
export interface FormImportBatchProgress {
  total: number;
  /** 1-base */
  current: number;
  currentSheetName?: string;
  completed: number;
  failed: number;
}

/** Job 진행률 — useJobPolling이 소비하는 형상. */
export interface FormImportProgress {
  /**
   * cancelled는 실패가 아닌 별도 종료 상태다 — 취소 시점까지 만들어진 초안은 남는다.
   * ⚠️ useJobPolling은 isComplete/isFailed 두 술어만 보므로 소비 측에서 cancelled를
   * 종료로 인식시키지 않으면 폴링이 영원히 돈다(useFormImportJob의 isComplete 참조).
   */
  status: 'processing' | 'completed' | 'failed' | 'cancelled';
  /** uploading(수신) → analyzing(AI) → validating(IR 검증) → creating(템플릿 생성) */
  stage: 'uploading' | 'analyzing' | 'validating' | 'creating';
  percent: number;
  /** 현재 시도 중(성공 시 사용된) AI provider */
  provider?: string;
  /** 현재 시도 중(성공 시 실제 분석에 쓰인) 모델명 — 상위 모델 override/강등 여부가 보인다 */
  model?: string;
  /**
   * 서버 기준 경과(ms) — 응답 시점에 파생된 상대값이다(절대 시각이 아니라 PC 시계 오차 무관).
   * 새로고침 복구 시 이 값에서 이어 세면 "방금 시작"으로 오해하지 않는다. 옛 job은 없음.
   */
  elapsedMs?: number;
  error?: string;
  result?: FormImportJobResult;
  /** 실패 시 마지막 AI 원문 — 진단용(화면에는 접어서 노출) */
  rawResponse?: string;
  /** 복수 시트 배치 진행 위치 — 항목 2개 이상일 때만 */
  batch?: FormImportBatchProgress;
  /** 항목별 결과 — 종료 시 채워진다(단건도 길이 1). 성공 0건에서도 실린다. */
  sheets?: FormImportSheetOutcome[];
  completedCount?: number;
  failedCount?: number;
  cancelledCount?: number;
  /** 취소 요청 접수됨(아직 반영 전) — "취소 중" 표시용 */
  cancelRequested?: boolean;
}

/** 활성 Job 조회 응답 — 없으면 null. */
export interface ActiveFormImportJob {
  jobId: string;
  data: FormImportProgress;
}

// ===== 데이터셋 엑셀 행 가져오기 (2026-08-09) =====
// 서버 dataset-xlsx.ts DatasetXlsxSheet와 1:1. 파싱 결과는 **원시 격자**뿐이고
// 헤더 판정·매핑·값 변환은 클라이언트(pages/report-composer/datasetExcelImport.ts) 몫이다.

/** 엑셀 시트 한 장의 원시 셀 격자. */
export interface DatasetXlsxSheet {
  name: string;
  /** 격자 행 수(후행 빈 행 절단 후). 빈 시트는 0. */
  rowCount: number;
  colCount: number;
  /** rowCount×colCount 직사각형(짧은 행은 ''로 패딩). 빈 셀은 ''. 값은 표시 문자열. */
  cells: string[][];
  /** 상한(1000행/100열) 절단 여부 — true면 "여기까지만 읽었다"를 사용자에게 알려야 한다. */
  truncatedRows: boolean;
  truncatedCols: boolean;
}

// ===== 데이터 연결 후보 제안 (form-import P2, 2026-07-27 결정) =====
// 서버 form-import.service.ts의 BindingSuggestField / bindingSuggestionsSchema와 1:1.

/** 매칭 대상 필드 1개 — 에디터가 현재 편집 상태에서 추출해 보낸다(미저장 변경 반영). */
export interface BindingSuggestField {
  bindingKey: string;
  /** 한글 라벨(작성폼/양식) — AI 매칭의 주 근거 */
  label?: string;
  /** 표 문맥(섹션 제목·인접 라벨 등) — 동음이의 해소 보조 */
  context?: string;
}

/** AI 매칭 후보 1건 — 서버가 카탈로그 실존(catalogKey/select) 재검증 후 반환한다. */
export interface BindingSuggestion {
  bindingKey: string;
  catalogKey: string;
  select: string;
  /** 0~1 확신도 */
  confidence: number;
  /** 매칭 이유 한 줄 */
  reason: string;
}

// ===== Composer 시뮬레이션(dry-run 합성) 타입 (2026-07-26 결정) =====

/** POST admin/report-composer/simulate-compose body — 서버 SimulateComposeDto에 대응. */
export interface SimulateComposeBody {
  templateKey: string;
  companyId: string;
  siteId?: string;
  /** 앵커 점검 ID 목록 — 지정 시 siteId 필수(서버가 현장 일치 정합 검증). */
  inspectionIds?: string[];
  /** 미지정 시 서버가 템플릿 name으로 대체. */
  title?: string;
  /** 작성 폼으로 채운 content — 실생성 dto.content와 동일 형상. */
  content: Record<string, unknown>;
  /** 작성자 컨텍스트 시뮬레이션(선택) — 실생성 authorId에 해당. */
  userId?: string;
}

/** simulate-compose 응답 — 실생성이 report 행에 남기는 것과 동일 구조(dry-run, 부수효과 0). */
export interface SimulateComposeResult {
  templateSnapshot: unknown;
  content: Record<string, unknown>;
  title: string;
  approvalLineDefinition: ApprovalLineDefinitionRow[] | null;
  /** live 바인딩(서명 등) 현재값 — 상세 응답 composedLiveData와 같은 형상(렌더 liveData 채널). */
  liveData: Record<string, unknown>;
}

// ===== 위젯 프리셋 v1 타입 (2026-07-17 결정) =====
// 서버 ComposerWidgetPreset / ComposerWidgetPresetVersion 행에 대응(Date는 JSON 직렬화로 ISO 문자열).

/** 프리셋 버전 스냅샷 — subtree=단일 루트 위젯(v1 범위). 발행 주체는 super_admins/users 분리라 nullable 2컬럼. */
export interface WidgetPresetVersion {
  id: string;
  presetId: string;
  version: number;
  /** 위젯 서브트리 스냅샷(단일 루트 위젯) */
  subtree: ComposerWidget;
  changeNote: string;
  publishedBySuperAdminId: string | null;
  publishedByUserId: string | null;
  createdAt: string;
}

/** 프리셋 라이브러리 행 — ownerCompanyId null=플랫폼(제공) 프리셋. */
export interface WidgetPreset {
  id: string;
  key: string;
  name: string;
  description: string | null;
  ownerCompanyId: string | null;
  currentVersion: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 단건 조회 응답 — 버전 목록(version desc, 스냅샷 포함) 동봉. */
export interface WidgetPresetWithVersions extends WidgetPreset {
  versions: WidgetPresetVersion[];
}

/** 프리셋 생성(=v1 발행) body — ownerCompanyId 미지정=플랫폼. */
export interface CreateWidgetPresetBody {
  /** 미지정 시 서버 자체 발급(`preset-<random8>`) — 저작 UI는 보내지 않음. */
  key?: string;
  name: string;
  description?: string;
  subtree: ComposerWidget;
  changeNote: string;
  ownerCompanyId?: string;
}

/** 새 버전 발행 body — currentVersion+1, 구버전 스냅샷 보존. */
export interface PublishWidgetPresetVersionBody {
  subtree: ComposerWidget;
  changeNote: string;
}

/** 이름/설명 수정 body — 버전 스냅샷 불변, 메타만 갱신. 두 필드 optional(빈 patch는 서버 400). */
export interface UpdateWidgetPresetMetaBody {
  name?: string;
  description?: string;
}

// Admin API
export const adminApi = {
  getSystemInfo: () =>
    fetchApi<{
      server: VersionInfo | null;
      web: VersionInfo | null;
      mobile: VersionInfo | null;
      admin: VersionInfo | null;
    }>('/admin/system-info'),

  getDashboard: () =>
    fetchApi<{
      totalOrganizations: number;
      totalUsers: number;
      totalSites: number;
      totalInspections: number;
      recentActivity: {
        organizationsThisWeek: number;
        usersThisWeek: number;
        inspectionsThisWeek: number;
      };
    }>('/admin/dashboard'),

  getSubscriptions: (params?: {
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    return fetchApi<
      Array<{
        /** 구독 id — 멤버십/구독 관리 endpoint(`/admin/subscriptions/:subscriptionId/*`)용. */
        id: string;
        /**
         * 회사 id — 조직을 가리키는 대부분의 endpoint(`companyId`/`organizationId` 파라미터)가
         * 기대하는 축. 서버는 원래 내려주고 있었으나 이 타입에 없어 화면들이 `id`(구독 id)를
         * 회사 id 자리에 넘겨 왔다.
         */
        companyId: string;
        name: string;
        category: OrganizationCategory;
        createdAt: string;
        _count: { members: number; sites: number };
      }>
    >(`/admin/subscriptions?${query}`);
  },

  getSites: (params?: {
    limit?: number;
    offset?: number;
    organizationId?: string;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.organizationId) query.set('organizationId', params.organizationId);
    if (params?.search) query.set('search', params.search);
    return fetchApi<
      Array<{
        id: string;
        name: string;
        address: string | null;
        addressDetail: string | null;
        industryType: string | null;
        industrySubType: string | null;
        /** 업종 분류(KSIC) — 정본. 표시는 `lib/siteIndustry`가 이것 다음 레거시 두 칸으로 폴백한다 */
        industryId: string | null;
        industry: AdminSiteIndustry | null;
        status: SiteStatus;
        createdAt: string;
        organization: {
          id: string;
          name: string;
          category: string;
        };
        _count: {
          inspections: number;
          assignments: number;
        };
      }>
    >(`/admin/sites?${query}`);
  },

  getSiteDetail: (siteId: string) =>
    fetchApi<{
      id: string;
      name: string;
      address: string | null;
      addressDetail: string | null;
      zonecode: string | null;
      latitude: number | null;
      longitude: number | null;
      timezone: string | null;
      industryType: string | null;
      industrySubType: string | null;
      industryId: string | null;
      industry: AdminSiteIndustry | null;
      hazardCategories: string[];
      status: SiteStatus;
      createdAt: string;
      updatedAt: string;
      organization: {
        id: string;
        name: string;
        category: string;
      } | null;
      assignments: Array<{
        id: string;
        /**
         * 배정 유형 — **정본은 `@safetyhub/shared`의 `SiteAssignmentType`**(서버 prisma enum 미러와 한 벌).
         * 종전 `string`이었고 그래서 오타·오값을 tsc가 못 잡았다 — 바로 아래 `isOwner` 주석이 같은 형태의
         * 결함(`memberRole: string`이 늘 `undefined`였는데 tsc가 침묵)을 기록하고 있다. admin은 Vite라 CI가
         * 타입을 보지 않으므로 이런 자리는 **좁혀 두는 것만이** 방어선이다(2026-09-27).
         * admin은 이 값을 **표시로만** 쓴다(`SiteDetailModal.tsx` 배지 — 담당자 유형 쓰기는 0곳).
         */
        assignmentType: SiteAssignmentType;
        userId: string;
        userName: string;
        /**
         * 소유자 성질(배정 축, P7-b-2 — 옛 `memberRole === 'OWNER'`). 서버 `admin.service.getSiteDetail`이
         * `withOwnerFlag`로 싣는다. ⚠️ 종전 이 자리는 `memberRole: string`이었는데 서버가 그 필드를 **주지 않아**
         * 값이 늘 `undefined`였고, 그래서 소유자도 회색 배지로 떨어졌다(2026-09-25 리뷰 적발 — 타입이 `string`이라
         * tsc가 침묵했고 admin은 Vite라 CI도 안 봤다).
         */
        isOwner: boolean;
        auths: Array<{ provider: string; providerId: string }>;
      }>;
      _count: {
        inspections: number;
        reports: number;
        tbms: number;
      };
    }>(`/admin/sites/${siteId}`),

  updateSite: (
    siteId: string,
    data: {
      name?: string;
      address?: string;
      addressDetail?: string;
      /**
       * 업종 분류(KSIC) `Industry.id` — `undefined` = 그대로 · `null` = 지움(서버 `UpdateAdminSiteDto`).
       * 레거시 자유 텍스트(`industryType`·`industrySubType`)는 화면이 더 이상 쓰지 않는다(2026-10-31).
       */
      industryId?: string | null;
      /** TRANSFER_PENDING은 이관 플로우 전용이라 직접 설정할 수 없다(서버 DTO도 3값만 받는다). */
      status?: SiteEditableStatus;
    },
  ) =>
    fetchApi<{
      id: string;
      name: string;
      address: string | null;
      addressDetail: string | null;
      industryType: string | null;
      industrySubType: string | null;
      industryId: string | null;
      industry: AdminSiteIndustry | null;
      status: SiteStatus;
      updatedAt: string;
    }>(`/admin/sites/${siteId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getUsers: (params?: {
    limit?: number;
    offset?: number;
    organizationId?: string;
    /** 이름·연락 이메일·전화·로그인 식별자 부분일치(서버). 탈퇴 사용자도 걸린다 */
    search?: string;
    /** 생략하면 전체 — `withdrawn`은 탈퇴 대기(완전삭제 포함) */
    status?: 'active' | 'withdrawn';
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    // 서버 getUsers는 organizationId를 companyId로 취급(H-8c) — 조직 스코프 필터.
    if (params?.organizationId) query.set('organizationId', params.organizationId);
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    return fetchApi<
      Array<{
        id: string;
        /**
         * `email`은 **연락 이메일**(Member.email)이다 — 결재 통지가 읽는 축이고, 로그인
         * 이메일(`auths` 중 provider='EMAIL')과는 다른 칸이다 (F-H).
         */
        member: { name: string; phone: string | null; email: string | null } | null;
        isSuperAdmin: boolean;
        createdAt: string;
        deletedAt: string | null;
        deletionSource: 'SELF' | 'ADMIN' | null;
        /** 전 문서 유형 접근 entitlement (조직 category와 무관하게 전 유형 노출·작성 허용) */
        allReportTypes: boolean;
        originalName: string | null;
        auths: Array<{
          provider: 'EMAIL' | 'ID' | 'KAKAO' | 'GOOGLE';
          providerId: string;
          verified: boolean;
        }>;
        memberships: Array<{
          /** 소유자 성질(배정 축, P7-b-2 — 옛 `role === 'OWNER'`) */
          isOwner: boolean;
          /**
           * 구성원 자격(배정 축). **false는 결측이 아니라 "외부 연락처"라는 뜻이다** — 현장 담당자·외부 결재자로
           * 등록된 Member의 CompanyMember 연결이며 조직 권한이 없다. 서버는 일부러 안 거른다 — 거르는 위치는
           * 소비 화면이고, 조직 구성원을 세는 화면은 `businessMemberships`(`lib/memberships`)를 지나야 한다.
           */
          countsAsMember: boolean;
          organization: { id: string; name: string; category: string };
        }>;
      }>
    >(`/admin/users?${query}`);
  },

  /**
   * 소유권 이관 대상 후보 검색 (S15 — 조직 밖 계정 지정)
   *
   * `getUsers`와 **다른 엔드포인트**인 이유: getUsers는 탈퇴·완전삭제 사용자를 일부러
   * 포함하고 `User.id` 축을 주는데, 이관은 그들을 제외한 `Member.id` 축이 필요하다.
   * 여기 오는 후보는 서버 `forceWithdrawUser`가 받아들이는 집합과 같다.
   *
   * ⚠️ 최소 검색어 길이 미만이면 서버가 조회 없이 빈 배열을 준다(meta.minQueryLength).
   */
  searchTransferTargetCandidates: (params: { q: string; excludeUserId?: string }) => {
    const query = new URLSearchParams();
    query.set('q', params.q);
    if (params.excludeUserId) query.set('excludeUserId', params.excludeUserId);
    return fetchApi<
      Array<{
        /** 이관이 쓰는 축 — orgActions[].transferToMemberId가 이 값이다 */
        memberId: string;
        userId: string | null;
        name: string;
        email: string | null;
        phone: string | null;
        /** 아이디 로그인(provider ID)의 식별자 */
        username: string | null;
        organizations: Array<{ id: string; name: string; isOwner: boolean }>;
      }>
    >(`/admin/users/transfer-target-candidates?${query}`);
  },

  // 사용자 직접 생성 (테스트용)
  createUser: (data: {
    name: string;
    phone: string;
    username: string;
    password: string;
    memberships?: Array<{
      /**
       * 회사 id — 서버 `CreateUserMembershipDto`는 `companyId`(구 `organizationId` alias)만
       * 받는다. `subscriptionId`라는 필드는 서버에 없어, 그 이름으로 보내면
       * ValidationPipe(forbidNonWhitelisted)가 400으로 막는다.
       */
      companyId?: string;
      newOrganization?: {
        name: string;
      };
      /** 시스템 역할 key(P7-b-2 — 옛 `role` enum 대체). 후보는 `useSystemRoleOptions` */
      roleKey: string;
    }>;
  }) =>
    fetchApi<{
      id: string;
      name: string;
      username: string;
      phone: string;
      createdAt: string;
      memberships: number;
      createdOrganizations: Array<{ id: string; name: string }>;
    }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  impersonate: (userId: string) =>
    fetchApi<{
      accessToken: string;
      targetUser: { id: string; email: string; member: { name: string } | null };
      expiresIn: number;
    }>(`/admin/impersonate/${userId}`, {
      method: 'POST',
    }),

  // 슈퍼관리자 관리
  getAdmins: () =>
    fetchApi<
      Array<{
        id: string;
        name: string;
        phone: string;
        createdAt: string;
        email: string | null;
        loginId: string | null;
        role?: { id: string; code: string; name: string } | null;
      }>
    >('/admin/admins'),

  createAdmin: (data: { email: string; name: string; password: string }) =>
    fetchApi<{
      id: string;
      email: string;
      name: string;
      isSuperAdmin: boolean;
      createdAt: string;
    }>('/admin/admins', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeAdmin: (adminId: string) =>
    fetchApi<{ success: boolean }>(`/admin/admins/${adminId}`, {
      method: 'DELETE',
    }),

  // 관리자 역할 할당
  assignAdminRole: (adminId: string, roleId: string | null) =>
    fetchApi<void>(`/admin/admins/${adminId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ roleId }),
    }),

  // 관리자 기본 정보 수정 (이름, 전화번호)
  updateAdmin: (adminId: string, data: { name?: string; phone?: string }) =>
    fetchApi<{ id: string; name: string; phone: string | null; updatedAt: string }>(
      `/admin/admins/${adminId}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),

  // 비밀번호 초기화 (SYSTEM_ADMIN 전용)
  resetAdminPassword: (adminId: string) =>
    fetchApi<{ tempPassword: string }>(`/admin/admins/${adminId}/reset-password`, {
      method: 'POST',
    }),

  // 본인 이메일 변경
  updateMyEmail: (data: { newEmail: string; password: string }) =>
    fetchApi<{ email: string }>('/admin/me/email', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // 본인 사용자 지정 ID 변경/추가
  updateMyLoginId: (data: { loginId: string; password: string }) =>
    fetchApi<{ loginId: string }>('/admin/me/login-id', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // 본인 비밀번호 변경
  changeMyPassword: (data: { currentPassword: string; newPassword: string }) =>
    fetchApi<{ success: boolean }>('/admin/me/password', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // 슈퍼관리자 초대
  createInvitation: (data: { email?: string; phone?: string; name?: string; roleId?: string }) =>
    fetchApi<{ id: string; token: string; expiresAt: string; invitationUrl: string; emailSent: boolean; smsSent: boolean }>(
      '/admin/invitations',
      { method: 'POST', body: JSON.stringify(data) },
    ),

  getInvitations: () =>
    fetchApi<
      Array<{
        id: string;
        email: string | null;
        phone: string | null;
        name: string | null;
        expiresAt: string;
        usedAt: string | null;
        createdAt: string;
        role: { id: string; name: string } | null;
        invitedBy: { id: string; name: string };
      }>
    >('/admin/invitations'),

  cancelInvitation: (id: string) =>
    fetchApi<null>(`/admin/invitations/${id}`, { method: 'DELETE' }),

  verifyInvitation: (token: string) =>
    fetchApi<{
      email: string | null;
      phone: string | null;
      name: string | null;
      roleName: string | null;
      expiresAt: string;
    }>(`/admin/invitations/verify/${token}`),

  acceptInvitation: (
    token: string,
    data: { password: string; name: string; phone: string; email?: string; loginId?: string },
  ) =>
    fetchApi<{ id: string; name: string; email: string | null }>(
      `/admin/invitations/accept/${token}`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  // 역할 관리
  getRoles: () =>
    fetchApi<
      Array<{
        id: string;
        code: string;
        name: string;
        description: string | null;
        isSystem: boolean;
        permissions: { permissionKey: string }[];
        _count?: { admins: number };
      }>
    >('/admin/roles'),

  createRole: (data: { code: string; name: string; description?: string; permissionKeys: string[] }) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      description: string | null;
      isSystem: boolean;
      permissions: { permissionKey: string }[];
    }>('/admin/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRole: (id: string, data: { name?: string; description?: string; permissionKeys?: string[] }) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      description: string | null;
      isSystem: boolean;
      permissions: { permissionKey: string }[];
    }>(`/admin/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteRole: (id: string) =>
    fetchApi<void>(`/admin/roles/${id}`, {
      method: 'DELETE',
    }),

  getPermissionKeys: () =>
    fetchApi<string[]>('/admin/permission-keys'),

  /**
   * 내 슈퍼관리자 권한 키. 타입을 `AdminPermissionKey[]`로 두는 이유는 소비처의 리터럴 판정(`.includes('admin-management')` 등)
   * 오타를 tsc red로 만들기 위해서다 — 서버는 `ADMIN_PERMISSION_KEYS`에서만 키를 주고, 사본 동기화는
   * `lib/__tests__/adminPermissionKeys.guard.test.ts`가 잰다.
   */
  getMyPermissions: () =>
    fetchApi<AdminPermissionKey[]>('/admin/me/permissions'),

  // 본인 정보 조회
  getMe: () =>
    fetchApi<{
      id: string;
      email: string;
      name: string;
      phone: string;
      isSuperAdmin: boolean;
      createdAt: string;
    }>('/admin/me'),

  // 본인 정보 수정
  updateProfile: (data: { name?: string; currentPassword?: string; newPassword?: string }) =>
    fetchApi<{
      id: string;
      email: string;
      name: string;
      isSuperAdmin: boolean;
    }>('/admin/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // 휴대폰 번호 변경 - 인증코드 발송
  requestPhoneChange: (newPhone: string) =>
    fetchApi<{
      message: string;
      expiresAt: string;
    }>('/admin/me/phone/request', {
      method: 'POST',
      body: JSON.stringify({ newPhone }),
    }),

  // 휴대폰 번호 변경 - 인증코드 검증 및 완료
  verifyPhoneChange: (newPhone: string, code: string) =>
    fetchApi<{
      message: string;
      phone: string;
    }>('/admin/me/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ newPhone, code }),
    }),

  // 사용자 전화번호 변경 (테스트용, 인증 없음)
  updateUserPhone: (userId: string, phone: string) =>
    fetchApi<{
      message: string;
      phone: string;
    }>(`/admin/users/${userId}/phone`, {
      method: 'PATCH',
      body: JSON.stringify({ phone }),
    }),

  // 사용자 이메일 변경 (소유권 인증 없음 — verified: false로 저장, 연락처 소급 보정용)
  updateUserEmail: (userId: string, email: string) =>
    fetchApi<{
      message: string;
      email: string;
    }>(`/admin/users/${userId}/email`, {
      method: 'PATCH',
      body: JSON.stringify({ email }),
    }),

  // 사용자 이름 변경
  updateUserName: (userId: string, name: string) =>
    fetchApi<{
      message: string;
      name: string;
    }>(`/admin/users/${userId}/name`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  // 전 문서 유형 접근 entitlement 토글 (결정 기록 2026-08-07-all-report-types-account-entitlement)
  updateUserAllReportTypes: (userId: string, allReportTypes: boolean) =>
    fetchApi<{
      message: string;
      allReportTypes: boolean;
    }>(`/admin/users/${userId}/all-report-types`, {
      method: 'PATCH',
      body: JSON.stringify({ allReportTypes }),
    }),

  // 사용자 비밀번호 변경
  updateUserPassword: (userId: string, newPassword: string) =>
    fetchApi<{
      message: string;
    }>(`/admin/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ newPassword }),
    }),

  // 사용자 강제 탈퇴 (Soft Delete)
  // orgActions: 조직(companyId)별 처분. transferToMemberId는 **Member.id(사람)** —
  // getCompanyMembers의 memberId를 그대로 쓴다 (companyMemberId 아님).
  forceWithdrawUser: (
    userId: string,
    dto: {
      /** 사유 유형 — 복원·재가입 가부를 가르는 축이라 서버에서 필수(누락 시 400) */
      reasonType: WithdrawalReasonType;
      reason?: string;
      orgActions?: Record<
        string,
        { action: OrgWithdrawalAction; transferToMemberId?: string }
      >;
      /**
       * 본인에게 탈퇴 통지 발송 (기본 false).
       * ⚠️ true라도 `reasonType: CLEANUP`일 때만 실제로 나간다 — 통지 문구가 30일 본인
       * 복구를 안내하는데 제재(SANCTION)는 본인 복원이 불가라 참이 아니다.
       */
      notifyUser?: boolean;
    },
  ) =>
    fetchApi<{
      message: string;
      userId: string;
      withdrawnAt: string;
      /** 실제 발송 여부 — 요청했는데 안 나갔을 수 있다(제재 유형·연락처 없음·발송 실패) */
      notified: boolean;
    }>(`/admin/users/${userId}/withdraw`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  /**
   * 제재 재가입 차단 해제 (번호 축)
   *
   * 차단 축이 전화번호뿐이라 그 번호를 정상적으로 넘겨받은 제3자도 함께 막힌다
   * (결정 기록 2026-08-20 정책 8이 감수한 대가). 신고가 들어오면 이 경로로 푼다.
   */
  releaseResignupBlock: (dto: { phone: string; reason?: string }) =>
    fetchApi<{ message: string; releasedCount: number }>(
      '/admin/resignup-blocks/release',
      {
        method: 'POST',
        body: JSON.stringify(dto),
      },
    ),

  // 복구 가능한 조직 목록 조회
  getRestorableOrganizations: (userId: string) =>
    fetchApi<{
      restorable: Array<{ id: string; name: string; category: string }>;
      skipped: Array<{ id: string; name: string; category: string; reason: string }>;
    }>(`/admin/users/${userId}/restorable-organizations`),

  // 복원 안내 재발송 — **탈퇴 철회보다 먼저 시도하는 경로**.
  // 링크가 계정 등록 연락처로만 가므로 본인 확인이 운영자 판단이 아니라 연락처 소유로 증명된다.
  // 그래서 수신처를 인자로 받지 않는다 (받으면 그 안전성이 사라진다).
  resendRestoreNotice: (userId: string) =>
    fetchApi<{
      message: string;
      channels: string[];
      remaining: number;
      recoveryDeadline: string;
    }>(`/admin/users/${userId}/resend-restore-notice`, { method: 'POST' }),

  // 사용자 복원 (탈퇴 철회)
  restoreUser: (
    userId: string,
    data?: {
      memberships?: Array<{
        companyId: string; // 서버 RestoreMembershipDto.companyId와 일치
        /** 시스템 역할 key(P7-b-2 — 옛 `role` enum 대체) */
        roleKey: string;
      }>;
    },
  ) =>
    fetchApi<{
      message: string;
      userId: string;
      restoredOrganizations: number;
      skippedOrganizations: number;
      addedMemberships: number;
    }>(`/admin/users/${userId}/restore`, {
      method: 'PATCH',
      body: JSON.stringify(data || {}),
    }),

  // 하드삭제(`DELETE /admin/users/:userId`)는 P8에서 서버·클라이언트 양쪽에서 제거됐다.
  // 정본은 강제 탈퇴(`forceWithdrawUser`) + 30일 뒤 USER_PURGE 익명화다.

  // 회사 멤버 목록 조회 (companyId 기준 — 소유권 이전 대상 선택용, 퇴사자 포함)
  // ⚠️ memberId는 Member.id(사람). getSubscriptionMembers가 같은 이름으로 CompanyMember.id를
  //    돌려주는 것과 축이 다르다 — 멤버십 행이 필요하면 companyMemberId를 쓸 것.
  getCompanyMembers: (companyId: string) =>
    fetchApi<
      Array<{
        memberId: string;
        companyMemberId: string;
        userId: string | null;
        name: string;
        /** 소유자 성질(활성 멤버십에만 참 — 퇴사자 행은 false) */
        isOwner: boolean;
        joinedAt: string;
        leftAt: string | null;
        isDeactivated: boolean;
      }>
    >(`/admin/companies/${companyId}/members`),

  // 구독(조직) 멤버 목록 조회
  getSubscriptionMembers: (subscriptionId: string) =>
    fetchApi<
      Array<{
        memberId: string;
        userId: string;
        name: string;
        /** 소유자 성질(배정 축, P7-b-2 — 옛 `role`) */
        isOwner: boolean;
        /** 구성원 자격 — false면 외부 연락처(옛 `role = null`) */
        countsAsMember: boolean;
        createdAt: string;
        isDeactivated: boolean;
        auths: Array<{
          provider: string;
          providerId: string;
        }>;
      }>
    >(`/admin/subscriptions/${subscriptionId}/members`),

  // 멤버십 히스토리 조회 (AuditLog 기반)
  getMembershipHistory: (subscriptionId: string) =>
    fetchApi<
      Array<{
        id: string;
        action: string;
        memberId: string | null;
        userName: string | null;
        userId: string | null;
        role: string | null;
        performedBy: string;
        performedAt: string;
        details: {
          oldValue: Record<string, unknown> | null;
          newValue: Record<string, unknown> | null;
        };
      }>
    >(`/admin/subscriptions/${subscriptionId}/membership-history`),

  // 구독(조직) 멤버 추가 — 역할은 시스템 역할 key(P7-b-2, 옛 `role` enum 대체)
  addSubscriptionMember: (subscriptionId: string, userId: string, roleKey: string) =>
    fetchApi<{
      memberId: string;
      userId: string;
      name: string;
      roleKey: string;
      isOwner: boolean;
      createdAt: string;
    }>(`/admin/subscriptions/${subscriptionId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, roleKey }),
    }),

  // 구독(조직) 멤버 제거
  removeSubscriptionMember: (subscriptionId: string, memberId: string) =>
    fetchApi<{
      message: string;
    }>(`/admin/subscriptions/${subscriptionId}/members/${memberId}`, {
      method: 'DELETE',
    }),

  // 구독(조직) 멤버 역할 변경 — `owner`면 소유자 지정, 그 외면 소유자 해제 + 그 역할 배정(P7-b-2)
  updateSubscriptionMemberRole: (subscriptionId: string, memberId: string, roleKey: string) =>
    fetchApi<{
      memberId: string;
      roleKey: string;
      isOwner: boolean;
    }>(`/admin/subscriptions/${subscriptionId}/members/${memberId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ roleKey }),
    }),

  // 회사 카테고리(타입) 변경
  updateCompanyCategory: (companyId: string, category: string) =>
    fetchApi<{
      id: string;
      category: string;
    }>(`/admin/companies/${companyId}/category`, {
      method: 'PATCH',
      body: JSON.stringify({ category }),
    }),

  // AI 프로바이더 관리
  getAIProviders: () =>
    fetchApi<
      Array<{
        id: string;
        provider: string;
        name: string | null;
        enabled: boolean;
        priority: number;
        apiKey: string;
        baseUrl: string | null;
        model: string | null;
        settings: Record<string, unknown> | null;
        usageTrackingMethod: 'direct' | 'provider_api';
        adminApiKey: string | null;
        createdAt: string;
        updatedAt: string;
      }>
    >('/admin/ai-providers'),

  createAIProvider: (data: {
    provider: string;
    name?: string;
    apiKey: string;
    enabled?: boolean;
    // priority는 자동으로 맨 후순위에 배치됨
    baseUrl?: string;
    model?: string;
    settings?: Record<string, unknown>;
    usageTrackingMethod?: 'direct' | 'provider_api';
    adminApiKey?: string;
  }) =>
    fetchApi<{
      id: string;
      provider: string;
      name: string | null;
      enabled: boolean;
      priority: number;
      usageTrackingMethod: 'direct' | 'provider_api';
    }>('/admin/ai-providers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAIProvider: (
    id: string,
    data: {
      name?: string;
      enabled?: boolean;
      // priority는 reorderAIProviders로 변경
      apiKey?: string;
      baseUrl?: string;
      model?: string;
      settings?: Record<string, unknown>;
      usageTrackingMethod?: 'direct' | 'provider_api';
      adminApiKey?: string;
    }
  ) =>
    fetchApi<{
      id: string;
      provider: string;
      name: string | null;
      enabled: boolean;
      priority: number;
      usageTrackingMethod: 'direct' | 'provider_api';
    }>(`/admin/ai-providers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  reorderAIProviders: (orderedIds: string[]) =>
    fetchApi<{ reordered: boolean }>('/admin/ai-providers/reorder', {
      method: 'PUT',
      body: JSON.stringify({ orderedIds }),
    }),

  deleteAIProvider: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/admin/ai-providers/${id}`, {
      method: 'DELETE',
    }),

  testAIProvider: (provider: string) =>
    fetchApi<{
      provider: string;
      connected: boolean;
      testedAt: string;
    }>(`/admin/ai-providers/${provider}/test`, {
      method: 'POST',
    }),

  testProviderUsageApi: (providerId: string) =>
    fetchApi<{
      providerId: string;
      usageApiConnected: boolean;
      testedAt: string;
    }>(`/admin/ai-providers/${providerId}/test-usage-api`, {
      method: 'POST',
    }),

  getAIUsage: (params?: {
    provider?: string;
    startDate?: string;
    endDate?: string;
    source?: 'direct' | 'provider_api' | 'auto';
  }) => {
    const query = new URLSearchParams();
    if (params?.provider) query.set('provider', params.provider);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.source) query.set('source', params.source);
    return fetchApi<{
      summary: {
        totalRequests: number;
        successfulRequests: number;
        failedRequests: number;
        totalTokens: number;
        totalCost: number;
        averageLatency: number;
        latencyByRequestType: Array<{
          requestType: string;
          label: string;
          count: number;
          avgLatencyMs: number;
          avgLatencySec: number;
          stddevLatencyMs: number;
          minRangeMs: number;
          maxRangeMs: number;
        }>;
      };
      byProvider: Record<
        string,
        {
          source: 'direct' | 'provider_api';
          requests: number;
          successfulRequests: number;
          failedRequests: number;
          tokens: { input: number; output: number; total: number };
          cost: number;
          averageLatency: number;
          models: Record<string, { requests: number; tokens: number; cost: number }>;
          latencyByRequestType: Array<{
            requestType: string;
            label: string;
            count: number;
            avgLatencyMs: number;
            avgLatencySec: number;
            stddevLatencyMs: number;
            minRangeMs: number;
            maxRangeMs: number;
          }>;
        }
      >;
      period: { start: string; end: string };
    }>(`/admin/ai-providers/usage?${query}`);
  },

  // AI 프롬프트 관리
  getAIPrompts: () =>
    fetchApi<
      Array<{
        id: string;
        name: string;
        type: string;
        industryType: string | null;
        systemPrompt: string;
        userPrompt: string;
        version: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
        createdBy: string | null;
      }>
    >('/admin/ai-prompts'),

  getAIPrompt: (id: string) =>
    fetchApi<{
      history: Array<{
        id: string;
        templateId: string;
        version: number;
        systemPrompt: string;
        userPrompt: string;
        modifiedBy: string | null;
        createdAt: string;
      }>;
    }>(`/admin/ai-prompts/${id}`),

  createAIPrompt: (data: {
    name: string;
    type: string;
    industryType?: string;
    systemPrompt: string;
    userPrompt: string;
  }) =>
    fetchApi<{
      id: string;
      name: string;
      type: string;
    }>('/admin/ai-prompts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAIPrompt: (
    id: string,
    data: {
      name?: string;
      systemPrompt?: string;
      userPrompt?: string;
    }
  ) =>
    fetchApi<{
      id: string;
      name: string;
      version: number;
    }>(`/admin/ai-prompts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteAIPrompt: (id: string) =>
    fetchApi<{ deactivated: boolean }>(`/admin/ai-prompts/${id}`, {
      method: 'DELETE',
    }),

  testAIPrompt: (id: string, data: { imageUrl: string; industryType?: string }) =>
    fetchApi<{
      promptId: string;
      result: {
        findings: Array<{
          type: string;
          severity: string;
          description: string;
          recommendation: string;
        }>;
        summary: string;
      };
      testedAt: string;
    }>(`/admin/ai-prompts/${id}/test`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  restoreAIPrompt: (id: string, version: number) =>
    fetchApi<{
      id: string;
      name: string;
      version: number;
    }>(`/admin/ai-prompts/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),

  // 예약 태스크 관리
  getTasks: (params?: {
    limit?: number;
    offset?: number;
    status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    type?: 'USER_PURGE' | 'ORGANIZATION_PURGE' | 'EXPORT_CLEANUP' | 'INSPECTION_PURGE';
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.status) query.set('status', params.status);
    if (params?.type) query.set('type', params.type);
    return fetchApi<
      Array<{
        id: string;
        type: 'USER_PURGE' | 'ORGANIZATION_PURGE' | 'EXPORT_CLEANUP' | 'INSPECTION_PURGE';
        targetId: string;
        targetType: string;
        targetInfo: { name: string; detail?: string } | null;
        scheduledAt: string;
        status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
        executedAt: string | null;
        result: string | null;
        retryCount: number;
        metadata: Record<string, unknown> | null;
        cancelledAt: string | null;
        cancelReason: string | null;
        createdAt: string;
        updatedAt: string;
        organizationsToDelete?: Array<{
          id: string;
          name: string;
          category: string;
          memberCount: number;
        }>;
      }>
    >(`/admin/tasks?${query}`);
  },

  getTaskStats: () =>
    fetchApi<{
      pending: number;
      processing: number;
      completed: number;
      failed: number;
      cancelled: number;
      byType: {
        USER_PURGE: number;
        ORGANIZATION_PURGE: number;
        EXPORT_CLEANUP: number;
        // 폐지된 타입(2026-10-07)이지만 서버가 enum 전수로 센다(Tasks.tsx `TaskType` JSDoc)
        INSPECTION_PURGE: number;
      };
    }>('/admin/tasks/stats'),

  getTask: (taskId: string) =>
    fetchApi<{
      id: string;
      type: 'USER_PURGE' | 'ORGANIZATION_PURGE' | 'EXPORT_CLEANUP' | 'INSPECTION_PURGE';
      targetId: string;
      targetType: string;
      targetInfo: { name: string; detail?: string } | null;
      scheduledAt: string;
      status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
      executedAt: string | null;
      result: string | null;
      retryCount: number;
      metadata: Record<string, unknown> | null;
      cancelledAt: string | null;
      cancelReason: string | null;
      createdAt: string;
      updatedAt: string;
    }>(`/admin/tasks/${taskId}`),

  cancelTask: (taskId: string, reason?: string) =>
    fetchApi<{
      message: string;
      taskId: string;
    }>(`/admin/tasks/${taskId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  retryTask: (taskId: string) =>
    fetchApi<{
      message: string;
      taskId: string;
    }>(`/admin/tasks/${taskId}/retry`, {
      method: 'POST',
    }),

  executeTask: (taskId: string) =>
    fetchApi<{
      message: string;
      taskId: string;
      status: 'COMPLETED' | 'FAILED';
      executedAt: string;
      result?: string;
    }>(`/admin/tasks/${taskId}/execute`, {
      method: 'POST',
    }),

  // ============================================
  // 업종 관리 (Industry)
  // ============================================
  getIndustries: (params?: {
    parentId?: string;
    level?: number;
    isActive?: boolean;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.parentId) query.set('parentId', params.parentId);
    if (params?.level !== undefined) query.set('level', String(params.level));
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
    if (params?.search) query.set('search', params.search);
    return fetchApi<
      Array<{
        id: string;
        code: string;
        name: string;
        description: string | null;
        parentId: string | null;
        level: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
        _count: { children: number; hazardMappings: number };
      }>
    >(`/admin/industries?${query}`);
  },

  // 깊이 제한 없는 재귀 트리(서버 `findTree`가 평면 조회 후 parentId로 조립한다 — KSIC는 5단계).
  getIndustriesTree: () => fetchApi<AdminIndustryTreeNode[]>('/admin/industries/tree'),

  getIndustryStats: () =>
    fetchApi<{
      total: number;
      active: number;
      inactive: number;
      byLevel: { level: number; count: number }[];
    }>('/admin/industries/stats'),

  getIndustry: (id: string) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      description: string | null;
      parentId: string | null;
      level: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
      parent: { id: string; code: string; name: string } | null;
      children: Array<{ id: string; code: string; name: string; isActive: boolean }>;
      hazardMappings: Array<{
        id: string;
        hazardType: { id: string; code: string; name: string };
        priority: number;
        percentage: number | null;
      }>;
    }>(`/admin/industries/${id}`),

  createIndustry: (data: {
    code: string;
    name: string;
    description?: string;
    parentId?: string;
    isActive?: boolean;
  }) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      level: number;
      isActive: boolean;
      createdAt: string;
    }>('/admin/industries', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateIndustry: (
    id: string,
    data: {
      code?: string;
      name?: string;
      description?: string;
      parentId?: string | null;
      isActive?: boolean;
    }
  ) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      level: number;
      isActive: boolean;
      updatedAt: string;
    }>(`/admin/industries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteIndustry: (id: string) =>
    fetchApi<{ success: boolean }>(`/admin/industries/${id}`, {
      method: 'DELETE',
    }),

  toggleIndustryActive: (id: string) =>
    fetchApi<{
      id: string;
      isActive: boolean;
    }>(`/admin/industries/${id}/toggle-active`, {
      method: 'PATCH',
    }),

  // ============================================
  // 위험유형 관리 (HazardType)
  // ============================================
  getHazardTypes: (params?: { parentId?: string; level?: number; isActive?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.parentId) query.set('parentId', params.parentId);
    if (params?.level !== undefined) query.set('level', String(params.level));
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
    return fetchApi<
      Array<{
        id: string;
        code: string;
        name: string;
        description: string | null;
        parentId: string | null;
        level: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
        _count: { children: number; industryMappings: number };
      }>
    >(`/admin/hazard-types?${query}`);
  },

  getHazardTypesTree: () =>
    fetchApi<
      Array<{
        id: string;
        code: string;
        name: string;
        description: string | null;
        level: number;
        isActive: boolean;
        _count: { children: number; industryMappings: number };
        children: Array<{
          id: string;
          code: string;
          name: string;
          description: string | null;
          level: number;
          isActive: boolean;
          _count: { children: number; industryMappings: number };
        }>;
      }>
    >('/admin/hazard-types/tree'),

  getHazardTypeStats: () =>
    fetchApi<{
      total: number;
      active: number;
      inactive: number;
      byLevel: { level: number; count: number }[];
    }>('/admin/hazard-types/stats'),

  getHazardType: (id: string) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      description: string | null;
      parentId: string | null;
      level: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
      parent: { id: string; code: string; name: string } | null;
      children: Array<{ id: string; code: string; name: string; isActive: boolean }>;
      industryMappings: Array<{
        id: string;
        industry: { id: string; code: string; name: string };
        priority: number;
        percentage: number | null;
      }>;
    }>(`/admin/hazard-types/${id}`),

  createHazardType: (data: {
    code: string;
    name: string;
    description?: string;
    parentId?: string;
    isActive?: boolean;
  }) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      level: number;
      isActive: boolean;
      createdAt: string;
    }>('/admin/hazard-types', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateHazardType: (
    id: string,
    data: {
      code?: string;
      name?: string;
      description?: string;
      parentId?: string | null;
      isActive?: boolean;
    }
  ) =>
    fetchApi<{
      id: string;
      code: string;
      name: string;
      level: number;
      isActive: boolean;
      updatedAt: string;
    }>(`/admin/hazard-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteHazardType: (id: string) =>
    fetchApi<{ success: boolean }>(`/admin/hazard-types/${id}`, {
      method: 'DELETE',
    }),

  toggleHazardTypeActive: (id: string) =>
    fetchApi<{
      id: string;
      isActive: boolean;
    }>(`/admin/hazard-types/${id}/toggle-active`, {
      method: 'PATCH',
    }),

  // ============================================
  // 업종-위험유형 매핑 (IndustryHazardMapping)
  // ============================================
  getIndustryHazardMappings: (params?: { industryId?: string; hazardTypeId?: string }) => {
    const query = new URLSearchParams();
    if (params?.industryId) query.set('industryId', params.industryId);
    if (params?.hazardTypeId) query.set('hazardTypeId', params.hazardTypeId);
    return fetchApi<
      Array<{
        id: string;
        industryId: string;
        hazardTypeId: string;
        priority: number;
        percentage: number | null;
        source: string | null;
        industry: { id: string; code: string; name: string };
        hazardType: { id: string; code: string; name: string };
        createdAt: string;
        updatedAt: string;
      }>
    >(`/admin/industry-hazard-mappings?${query}`);
  },

  getIndustryMappings: (industryId: string) =>
    fetchApi<
      Array<{
        id: string;
        hazardTypeId: string;
        priority: number;
        percentage: number | null;
        source: string | null;
        hazardType: { id: string; code: string; name: string; description: string | null };
      }>
    >(`/admin/industries/${industryId}/hazard-mappings`),

  createIndustryHazardMapping: (data: {
    industryId: string;
    hazardTypeId: string;
    priority?: number;
    percentage?: number;
    source?: string;
  }) =>
    fetchApi<{
      id: string;
      industryId: string;
      hazardTypeId: string;
      priority: number;
      percentage: number | null;
      source: string | null;
    }>('/admin/industry-hazard-mappings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateIndustryHazardMapping: (
    id: string,
    data: {
      priority?: number;
      percentage?: number;
      source?: string;
    }
  ) =>
    fetchApi<{
      id: string;
      priority: number;
      percentage: number | null;
      source: string | null;
    }>(`/admin/industry-hazard-mappings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteIndustryHazardMapping: (id: string) =>
    fetchApi<{ success: boolean }>(`/admin/industry-hazard-mappings/${id}`, {
      method: 'DELETE',
    }),

  reorderIndustryHazardMappings: (industryId: string, mappingIds: string[]) =>
    fetchApi<{
      updated: number;
    }>('/admin/industry-hazard-mappings/reorder', {
      method: 'POST',
      body: JSON.stringify({ industryId, mappingIds }),
    }),

  // ============================================
  // 위험성평가 설정 (RiskAssessmentConfig)
  // ============================================
  getRiskAssessmentConfigs: () =>
    fetchApi<
      Array<{
        id: string;
        name: string;
        matrixType: string;
        methodType?: string;
        likelihoodCriteria: Array<{ level: number; label: string; description: string }>;
        severityCriteria: Array<{ level: number; label: string; description: string }>;
        // 등급 경계표 단일 정본 — 3단계도 여기에 저장한다(level/description 포함, 결정 2026-08-05)
        riskLevelCriteria: Array<{
          min: number;
          max: number;
          label: string;
          managementAction: string;
          level?: number;
          description?: string;
          color?: string;
        }>;
        /** @deprecated 옛 3단계 기준 — 읽기 폴백 전용 */
        levelCriteria?: Array<{
          level: number;
          label: string;
          description: string;
          managementAction?: string;
          color?: string;
        }>;
        isDefault: boolean;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>
    >('/admin/risk-assessment-configs'),

  getDefaultRiskAssessmentConfig: () =>
    fetchApi<{
      id: string;
      name: string;
      matrixType: string;
      likelihoodCriteria: Array<{ level: number; label: string; description: string }>;
      severityCriteria: Array<{ level: number; label: string; description: string }>;
      riskLevelCriteria: Array<{ min: number; max: number; label: string; managementAction: string }>;
      isDefault: boolean;
      isActive: boolean;
    }>('/admin/risk-assessment-configs/default'),

  getRiskAssessmentConfig: (id: string) =>
    fetchApi<{
      id: string;
      name: string;
      matrixType: string;
      likelihoodCriteria: Array<{ level: number; label: string; description: string }>;
      severityCriteria: Array<{ level: number; label: string; description: string }>;
      riskLevelCriteria: Array<{ min: number; max: number; label: string; managementAction: string }>;
      isDefault: boolean;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/admin/risk-assessment-configs/${id}`),

  createRiskAssessmentConfig: (data: {
    name: string;
    matrixType?: string;
    methodType?: string;
    likelihoodCriteria: Array<{ level: number; label: string; description: string }>;
    severityCriteria: Array<{ level: number; label: string; description: string }>;
    // ⚠️ 등급 번호(level)는 **없다** — 서버 RiskLevelCriterionDto에 없는 필드라 전역
    // ValidationPipe(whitelist + forbidNonWhitelisted)가 중첩 원소까지 400으로 거부한다.
    // 여기 optional로 남겨 두면 그런 payload를 타입 시스템이 승인해버린다(2026-08-06 리뷰).
    riskLevelCriteria: Array<{
      min: number;
      max: number;
      label: string;
      managementAction: string;
      description?: string;
      color?: string;
    }>;
    isDefault?: boolean;
    isActive?: boolean;
  }) =>
    fetchApi<{
      id: string;
      name: string;
      matrixType: string;
      isDefault: boolean;
      isActive: boolean;
    }>('/admin/risk-assessment-configs', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRiskAssessmentConfig: (
    id: string,
    data: {
      name?: string;
      matrixType?: string;
      methodType?: string;
      likelihoodCriteria?: Array<{ level: number; label: string; description: string }>;
      severityCriteria?: Array<{ level: number; label: string; description: string }>;
      // 등급 번호(level) 없음 — create와 같은 이유(서버 DTO 미보유 → forbidNonWhitelisted 400).
      riskLevelCriteria?: Array<{
        min: number;
        max: number;
        label: string;
        managementAction: string;
        description?: string;
        color?: string;
      }>;
      isActive?: boolean;
    }
  ) =>
    fetchApi<{
      id: string;
      name: string;
      matrixType: string;
      isActive: boolean;
    }>(`/admin/risk-assessment-configs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteRiskAssessmentConfig: (id: string) =>
    fetchApi<{ success: boolean }>(`/admin/risk-assessment-configs/${id}`, {
      method: 'DELETE',
    }),

  setDefaultRiskAssessmentConfig: (id: string) =>
    fetchApi<{
      id: string;
      name: string;
      isDefault: boolean;
    }>(`/admin/risk-assessment-configs/${id}/set-default`, {
      method: 'PATCH',
    }),

  toggleRiskAssessmentConfigActive: (id: string) =>
    fetchApi<{
      id: string;
      isActive: boolean;
    }>(`/admin/risk-assessment-configs/${id}/toggle-active`, {
      method: 'PATCH',
    }),

  // ============================================
  // 점검 관리 (Inspection)
  // ============================================
  getInspections: (params?: {
    limit?: number;
    offset?: number;
    organizationId?: string;
    siteId?: string;
    status?: string;
    includeDeleted?: boolean;
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.organizationId) query.set('organizationId', params.organizationId);
    if (params?.siteId) query.set('siteId', params.siteId);
    if (params?.status) query.set('status', params.status);
    if (params?.includeDeleted) query.set('includeDeleted', 'true');
    return fetchApi<
      Array<{
        id: string;
        status: string;
        date: string;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
        organization: { id: string; name: string; category: string };
        site: { id: string; name: string };
        inspector: { id: string; deletedAt: string | null; member: { name: string } | null };
        report: {
          id: string;
          title: string;
          status: string;
          pdfStatus: string;
          fileUrl: string | null;
          pdfError: string | null;
        } | null;
        _count: { photos: number; collaborators: number; findings: number };
        sequenceNumber: number;
      }>
    >(`/admin/inspections?${query}`);
  },

  getInspection: (id: string) =>
    fetchApi<{
      id: string;
      status: string;
      date: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string | null;
      organization: { id: string; name: string; category: string };
      site: { id: string; name: string; address: string | null };
      inspector: { id: string; deletedAt: string | null; member: { name: string } | null };
      reports: Array<{
        id: string;
        title: string;
        status: string;
        pdfStatus: string;
        fileUrl: string | null;
        pdfError: string | null;
        createdAt: string;
      }>;
      report: {
        id: string;
        title: string;
        status: string;
        pdfStatus: string;
        fileUrl: string | null;
        pdfError: string | null;
        createdAt: string;
      } | null;
      /**
       * ⚠️ `deletedAt`·`grade`·`purpose`는 ML 조회(P5)를 위해 2026-10-07에 더한 것이다 —
       * 점검 상세는 **지운 사진도 보여주고 품질 등급으로 가른다**. 형상 정본은
       * `lib/ml-sources.ts`의 `AdminMlPhoto`이고 여기 선언이 그것과 어긋나면 안 된다.
       */
      photos: Array<{
        id: string;
        url: string;
        thumbnailUrl: string | null;
        analysisStatus: string;
        createdAt: string;
        deletedAt: string | null;
        grade: MlPhotoGrade;
        purpose: PhotoPurpose;
      }>;
      /**
       * 그 점검을 `sourceInspectionId`로 가리키는 개선 이미지(P11).
       * `sourceReportId`가 null이면 출처 보고서가 삭제된 것 — 화면 문구는
       * `SOURCE_REPORT_DELETED_LABEL`을 쓴다(ML 목록과 같은 문구).
       */
      improvementImages: Array<{
        id: string;
        imageUrl: string;
        mimeType: string;
        summaryText: string;
        aiModel: string;
        usageCount: number;
        createdAt: string;
        sourceReportId: string | null;
        sourcePhotoId: string | null;
        hasEmbedding: boolean;
      }>;
      collaborators: Array<{
        id: string;
        role: string;
        user: { id: string; member: { name: string } | null };
      }>;
      _count: { photos: number; findings: number };
    }>(`/admin/inspections/${id}`),

  // ===== 보고서 관리 (전 조직 read-only) =====
  getReports: (params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    siteId?: string;
    status?: string;
    from?: string;
    to?: string;
    q?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.companyId) query.set('companyId', params.companyId);
    if (params?.siteId) query.set('siteId', params.siteId);
    if (params?.status) query.set('status', params.status);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.q) query.set('q', params.q);
    return fetchApi<{
      items: Array<{
        id: string;
        title: string;
        /** NOT NULL 컬럼 — 상세 `getReport`와 같은 정정(라운드 6). */
        templateKey: string;
        templateName: string | null;
        status: string;
        createdAt: string;
        company: { id: string; name: string } | null;
        site: { id: string; name: string } | null;
        authorName: string | null;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/admin/reports?${query}`);
  },

  getReport: (id: string) =>
    fetchApi<{
      id: string;
      title: string;
      status: string;
      /** `reports.template_key`는 NOT NULL(모든 문서가 Composer 양식) — null 선언은 구 유형 축 잔재였다(라운드 6 정정). */
      templateKey: string;
      templateName: string | null;
      templateSnapshot: unknown;
      content: unknown;
      companyId: string;
      siteId: string | null;
      fileUrl: string | null;
      pdfStatus: string;
      createdAt: string;
      updatedAt: string;
      company: { id: string; name: string } | null;
      site: { id: string; name: string } | null;
      authorName: string | null;
      composedLiveData: Record<string, unknown> | null;
      // 결재란(approvalBlock special) 표시 입력 — buildComposerApprovalData가 소비.
      approvalLineDefinition: ApprovalLineDefinitionRow[] | null;
      approvalSignatures: AdminApprovalSignature[] | null;
      authorSignerName: string | null;
      authorSignatureData: string | null;
      /** 작성 완료 시각 — F-25·F-34 게이트 재료(web 인증 상세와 같은 컬럼, 2026-09-18 R6). */
      authorSignedAt: string | null;
      /** 위임 담당자 존재 여부 — 공개 계약과 같은 `{ id }` 형상(코어는 존재만 읽는다). 비위임이면 null. */
      delegatedContact: { id: string } | null;
      approvalSignatureMode: ApprovalSignatureMode | null;
    }>(`/admin/reports/${id}`),

  resetInspectionPdf: (inspectionId: string) =>
    fetchApi<{ message: string; reportId: string }>(`/admin/inspections/${inspectionId}/reset-pdf`, {
      method: 'POST',
    }),

  resetReportPdf: (reportId: string) =>
    fetchApi<{ message: string; reportId: string }>(`/admin/reports/${reportId}/reset-pdf`, {
      method: 'POST',
    }),

  // PDF 상태 초기화 (테스트용)
  resetPdfStatus: (reportId: string, status: 'NONE' | 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED') =>
    fetchApi<{ message: string; reportId: string; oldStatus: string; newStatus: string }>(
      `/admin/reports/${reportId}/reset-pdf-status`,
      {
        method: 'POST',
        body: JSON.stringify({ status }),
      }
    ),

  // 결재 상황 조회
  getReportApprovals: (reportId: string) =>
    fetchApi<{
      reportId: string;
      reportTitle: string;
      reportStatus: string;
      rounds: Array<{
        round: number;
        signatures: Array<{
          id: string;
          signerName: string;
          signerTitle: string | null;
          approvalLevel: number;
          status: 'PENDING' | 'APPROVED' | 'REJECTED';
          signedAt: string | null;
          comment: string | null;
        }>;
      }>;
    }>(`/admin/reports/${reportId}/approvals`),

  // 결재 상태 초기화
  resetApprovalSignature: (signatureId: string) =>
    fetchApi<{
      message: string;
      signatureId: string;
      reportId: string;
    }>(`/admin/approvals/${signatureId}/reset`, {
      method: 'POST',
    }),

  // 결재 라운드 삭제
  deleteApprovalRound: (reportId: string, round: number) =>
    fetchApi<{
      message: string;
      reportId: string;
      deletedRound: number;
      deletedSignatureCount: number;
    }>(`/admin/reports/${reportId}/approval-round/${round}`, {
      method: 'DELETE',
    }),

  // ============================================
  // 일일 사용량 관리 (Daily Usage)
  // ============================================
  getUsageConfig: () =>
    fetchApi<{
      id: string;
      photoLimit: number;
      inspectionLimit: number;
      isEnabled: boolean;
      updatedAt: string;
    }>('/admin/usage/config'),

  updateUsageConfig: (data: {
    photoLimit?: number;
    inspectionLimit?: number;
    isEnabled?: boolean;
  }) =>
    fetchApi<{
      id: string;
      photoLimit: number;
      inspectionLimit: number;
      isEnabled: boolean;
      updatedAt: string;
    }>('/admin/usage/config', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getUsersUsage: (params?: { date?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    return fetchApi<{
      config: {
        photoLimit: number;
        inspectionLimit: number;
        isEnabled: boolean;
      };
      date: string;
      usages: Array<{
        id: string;
        userId: string;
        userName: string;
        userIdentifier: string | null;
        photoCount: number;
        inspectionCount: number;
        lastResetAt: string | null;
        resetBy: string | null;
        createdAt: string;
      }>;
      total: number;
    }>(`/admin/usage/users?${query}`);
  },

  getUserUsageHistory: (userId: string, limit?: number) => {
    const query = new URLSearchParams();
    if (limit) query.set('limit', String(limit));
    return fetchApi<{
      user: {
        id: string;
        name: string;
        identifier: string | null;
      } | null;
      history: Array<{
        date: string;
        photoCount: number;
        inspectionCount: number;
        lastResetAt: string | null;
        resetBy: string | null;
      }>;
    }>(`/admin/usage/users/${userId}?${query}`);
  },

  resetUserUsage: (userId: string) =>
    fetchApi<{
      message: string;
      userId: string;
      resetBy: string;
      resetAt: string;
    }>(`/admin/usage/users/${userId}/reset`, {
      method: 'POST',
    }),

  updateUserUsage: (
    userId: string,
    data: { photoCount?: number; inspectionCount?: number },
  ) =>
    fetchApi<{
      userId: string;
      photoCount: number;
      inspectionCount: number;
      updatedBy: string;
      updatedAt: string;
    }>(`/admin/usage/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // ============================================
  // 릴리즈 노트 (Release Notes) — **조회 2 + 공개 여부 전환 1뿐이다**
  //
  // 저작 메서드 4개(`createReleaseNote`·`updateReleaseNote`·`deleteReleaseNote`·
  // `mergeReleaseNotes`)는 2026-10-14에 지웠다. 서버 쪽 라우트도 함께 사라졌으므로
  // 되살려도 404다.
  //
  // **노트의 정본은 `release-notes/notes/YYYY-MM-DD.json` 파일**이고 DB는 파생이다 — 반영은
  // `.github/workflows/import-release-notes.yml`이 한다. 문구를 고치려면 그 파일을 고쳐 push한다.
  // 사유 전문은 서버 `AdminReleaseNotesController`의 클래스 JSDoc + 결정 기록
  // `docs/decisions/2026-10-14-release-notes-authoring-canonical-source-and-popup.md` §②.
  //
  // 「틀린 문구를 즉시 내리는」 일은 `bulkUpdateReleaseNoteStatus`가 한다(숨김 `ARCHIVED` /
  // 복구 `PUBLISHED`) — 단일 항목도 `[id]`로 그것을 쓴다.
  //
  // ⚠️ surface 유니온은 {@link ReleaseNotePlatform} **한 자리**가 소유한다 — 정본은 여전히 서버
  // Prisma enum이고 둘을 묶는 동기화 수단이 없지만, 적어도 admin 안에서는 사본이 하나다
  // (2026-10-15에 이 파일 세 자리 + `pages/ReleaseNotes.tsx`의 `Platform` 넷을 그 하나로 접었다).
  // enum이 늘면 **그 타입 선언 하나**와 `lib/releaseNoteDisplay.ts`의 `PLATFORM_LABELS`·
  // `PLATFORM_COLORS` 두 매핑을 고친다(2026-10-21에 그 둘이 `pages/ReleaseNotes.tsx`에서 그
  // 파일로 옮겨졌다 — **소비 뷰와 공용**이 되었기 때문이고, 그래서 고칠 자리는 여전히 한 벌이다).
  // admin은 Vite(esbuild) 빌드라 **CI가 타입을 보지 않으므로** 로컬 tsc로 닫을 것.
  // ============================================
  getReleaseNotes: (params?: {
    platform?: ReleaseNotePlatform;
    category?: 'FEATURE' | 'IMPROVEMENT' | 'BUGFIX' | 'SECURITY';
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    limit?: number;
    cursor?: string;
    /**
     * 검색어 — 제목·본문 부분 일치 또는 커밋 해시 앞부분(매칭 규칙의 정본은 서버
     * `release-notes.service.ts`의 `buildSearchConditions`). 서버가 trim하고 100자 넘으면 400이다
     * (`RELEASE_NOTES_SEARCH_MAX_LENGTH`).
     */
    q?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.platform) query.set('platform', params.platform);
    if (params?.category) query.set('category', params.category);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.q) query.set('q', params.q);
    return fetchApi<{
      items: Array<{
        id: string;
        title: string;
        content: string | null;
        /**
         * surface **집합** — 판정의 정본이다(서버 `release_notes.platforms`).
         *
         * ⚠️ 응답에는 **처음부터 있었다** — 서버 `findAll`이 Prisma 행을 그대로 돌려주고
         * `ReleaseNoteResponse`가 그 모델의 **별칭**이기 때문이다(그 타입 JSDoc). 빠져 있던 것은
         * 이 손열거 타입 하나이고, 그래서 화면이 아래 `platform`(대표값)으로 칩을 그렸다 —
         * **필터는 집합 축**(`platforms: { has: … }`)인데 **표시는 대표값 축**이라 운영자에게
         * 「필터와 안 맞는 행」으로 읽혔다. prod 실측으로 `ADMIN` 축만 **108건**이 그 상태였다
         * (분모 1506행 — 서버 `release-notes.service.ts` `findAll`의 `where` 주석).
         * ⇒ **표시는 반드시 이 칸을 읽는다**(`pages/ReleaseNotes.tsx`의 칩).
         *
         * ⚠️ **빈 배열이 올 수 있다**(`@default([])`) — 서버 schema 주석은 그것을 **결함**이라
         * 적는다(필터 조회의 `platforms && ARRAY[…]`에서 그 행이 영영 빠진다). 운영자 화면은 그
         * 상태를 가리지 않는다 — 처리는 `pages/ReleaseNotes.tsx`가 소유한다.
         */
        platforms: ReleaseNotePlatform[];
        /**
         * ⚠️ **대표값이다 — 표시에 읽지 말 것**(위 `platforms`를 읽는다). 불변식은
         * `platform = sorted(platforms)[0]`이고(`schema.prisma`의 컬럼 주석) 서버가 여전히 그
         * 칸을 돌려주므로 **선언에서 빼지 않는다** — 빼면 「준다고 선언한 것」이 응답보다 좁아
         * 그 어긋남을 아무도 재지 않는다(같은 사유가 `ReleaseNoteResponse` JSDoc에 있다).
         */
        platform: ReleaseNotePlatform;
        /**
         * 노출 대상 역할 집합 — `<네임스페이스>:<역할>`(`company:owner`·`sa:SYSTEM_ADMIN`).
         * 규약·판정의 정본은 shared `features/audience-targeting.ts`.
         *
         * ⚠️ **빈 배열이 「모두」이고 그것이 기본값이다** — `platforms`의 빈 배열과 **뜻이 반대**다
         * (그쪽은 결함이라 화면이 빨갛게 보인다). 둘을 같은 처분으로 그리지 말 것.
         * ⚠️ 「아무에게도」는 이 칸이 아니라 `platforms`의 `INTERNAL`이고 그 축은 **노트 파일**이
         * 소유한다 — admin에서 바꿀 수 없다.
         * ⚠️ 이 칸은 **DB가 소유한다**(`status`와 같은 층) — 노트 파일 스키마에 없으므로 import가
         * 건드리지 않는다. 그래서 {@link bulkUpdateReleaseNoteAudiences}가 저작 드리프트를 만들지
         * 않는다(사유 전문은 서버 `AdminReleaseNotesController.bulkUpdateAudiences` JSDoc).
         */
        audiences: string[];
        category: 'FEATURE' | 'IMPROVEMENT' | 'BUGFIX' | 'SECURITY';
        status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
        releasedAt: string;
        version: string | null;
        commitHash: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      nextCursor: string | null;
      total: number;
    }>(`/admin/release-notes?${query}`);
  },

  /**
   * **소비 뷰 목록** — `GET /admin/release-note-feed`. 2026-10-21 결정 채택 ⑥.
   *
   * 호출부는 `pages/ReleaseNoteFeed.tsx` 하나다. 위 {@link getReleaseNotes}(관리 목록)와 **다른
   * 라우트**인 것이 요점이고, 셋이 다르다:
   *  - **권한**: 관리는 `system-settings` 뒤 · 이쪽은 **수퍼관리자 전원**(서버가 그 클래스에
   *    `@RequirePermission`을 두지 않는다)
   *  - **집합**: 서버가 `status: PUBLISHED` ∧ 소비 뷰어가 있는 surface로 **무조건** 좁힌다
   *    (`INTERNAL` 전용 노트와 숨긴 노트가 빠진다)
   *  - **쓰기**: 없다
   *
   * ⚠️ **`status`를 이 params에 더하지 말 것** — 서버 DTO에 그 칸이 **없어** 보내면 `400`이다
   * (`forbidNonWhitelisted`). 그것이 의도다: 받아서 무시하면 운영자는 필터가 걸린 줄 안다
   * (사유 전문은 서버 `AdminReleaseNoteFeedQueryDto` JSDoc). 숨김·복구는 **관리 화면**의 일이다.
   *
   * ⚠️ 응답 형상은 관리 목록과 **같다**(`findConsumerFeed`가 같은 `ReleaseNoteListResponse`를
   * 돌려준다) — 그래서 타입을 따로 쓰지 않고 `ReturnType`으로 묶지도 않았다. 두 라우트의 형상이
   * 갈리는 날 **여기 선언을 그때 가른다**(지금 미리 가르면 같은 것을 두 벌로 적는 것이다).
   */
  getReleaseNoteFeed: (params?: {
    platform?: ReleaseNotePlatform;
    category?: 'FEATURE' | 'IMPROVEMENT' | 'BUGFIX' | 'SECURITY';
    limit?: number;
    cursor?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.platform) query.set('platform', params.platform);
    if (params?.category) query.set('category', params.category);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.cursor) query.set('cursor', params.cursor);
    return fetchApi<{
      items: Array<{
        id: string;
        title: string;
        content: string | null;
        /** surface **집합** — 표시는 이 칸을 읽는다(대표값 `platform`이 아니다). */
        platforms: ReleaseNotePlatform[];
        /** ⚠️ **대표값이다 — 표시에 읽지 말 것**(위 `platforms`를 읽는다). */
        platform: ReleaseNotePlatform;
        /**
         * 노출 대상 역할 집합 — **이 화면은 읽지 않는다**(편집은 관리 화면의 일이다). 선언에서
         * 빼지 않는 이유는 위 {@link getReleaseNotes}와 같다: 빼면 「준다고 선언한 것」이 응답보다
         * 좁아 그 어긋남을 아무도 재지 않는다.
         */
        audiences: string[];
        category: 'FEATURE' | 'IMPROVEMENT' | 'BUGFIX' | 'SECURITY';
        /**
         * ⚠️ 이 피드에는 `PUBLISHED`만 온다(서버 무조건항) — 그래도 선언은 넓게 둔다. 좁혀 적으면
         * 서버가 그 축을 바꾼 날 **타입이 거짓**이 되는데 admin은 CI가 타입을 보지 않아 조용하다.
         */
        status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
        releasedAt: string;
        version: string | null;
        commitHash: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      nextCursor: string | null;
      total: number;
    }>(`/admin/release-note-feed?${query}`);
  },

  // 단수 상세 조회(`getReleaseNote` → `GET /admin/release-notes/:id`)는 2026-10-16에 지웠다 —
  // 최초 기능 커밋(2026-01-29)부터 **호출부가 0건**이었고 서버 라우트도 함께 지웠다. admin은 목록
  // 한 화면뿐이고(`pages/ReleaseNotes.tsx`) 위 `getReleaseNotes`가 이미 행 전체를 싣는다.
  // 되살리려면 **상세 화면이 먼저다** — 사유는 서버 `AdminReleaseNotesController` 클래스 JSDoc.

  /**
   * 공개 여부 전환 — 숨김 `ARCHIVED` / 복구 `PUBLISHED`. **이 모듈의 유일한 쓰기다.**
   *
   * ⚠️ `'DRAFT'`를 되살리지 말 것 — 서버 DTO가 `@IsIn([PUBLISHED, ARCHIVED])`로 **400**을 낸다
   * (사유는 서버 `RELEASE_NOTE_VISIBILITY_STATUSES` JSDoc: 저작이 DB에서 일어나지 않으니
   * 「초안」이라는 라벨이 참이 될 수 없다).
   */
  bulkUpdateReleaseNoteStatus: (ids: string[], status: 'PUBLISHED' | 'ARCHIVED') =>
    fetchApi<{ updated: number }>('/admin/release-notes/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    }),

  /**
   * 노출 대상(역할) 설정 — **전체 교체**다(더하기·빼기가 아니다).
   *
   * 빈 배열을 보내면 「모두에게」로 **되돌린다** — 그래서 이 한 함수가 설정과 해제를 둘 다 한다
   * (`bulkUpdateReleaseNoteStatus`가 숨김·복구를 한 라우트로 하는 것과 같은 형상). 단일 항목도
   * `[id]`로 이것을 쓴다.
   *
   * ⚠️ 접두(`company:`·`sa:`)는 **UI가 붙인다** — `lib/audience-options.ts`의 `buildAudienceRef`.
   * 접두 없는 값을 보내면 서버 DTO가 **400**으로 거부한다(판정자는 shared `isAudienceRef`).
   * ⚠️ **제목·내용·`platforms`를 이 함수에 더하지 말 것** — 그 축은 노트 파일이 소유하고, 여기서
   * 쓰면 2026-10-14에 닫은 저작 드리프트가 되돌아온다(서버 컨트롤러 클래스 JSDoc).
   *
   * ⚠️ **이 블록을 둘로 쪼개지 말 것**(2026-10-21 정정) — 종전에는 위 경고와 아래 `@param`이
   * **연속된 두 JSDoc 블록**이었고(둘 다 별표 두 개로 시작하는 별개 블록), 그러면 선언에 붙는
   * 것은 **뒤 블록 하나**라 툴팁·IDE hover에 위 저작 드리프트 경고가 **호출부에서 안 보였다**.
   * tsc·eslint 어느 쪽도 이 축을 보지 않는다. `@param`을 더할 일이 있으면 이 블록 **끝에** 붙인다.
   * ⚠️ 그리고 **블록 주석 안에 주석 종료 기호를 리터럴로 쓰지 말 것** — 그 자리에서 주석이 닫혀
   * 아래 코드가 통째로 파싱 오류가 된다(이 문단을 쓰다 실제로 그렇게 됐고 tsc가 160건을 냈다).
   *
   * @param acknowledgeUnconstrained 「닿는데 좁히지 않은 체계의 **전원**이 대상이 된다」를 운영자가
   *   확인했나. 없으면 서버가 **400**(`AUDIENCE_NAMESPACE_UNCONSTRAINED`)을 낸다 — 공개 노트에
   *   한쪽 체계만 겨누는 저작이 거의 항상 그 상태다.
   *
   *   ⚠️ `?? false`로 **채우지 말 것** — 기본값의 소유자는 **서버 하나**다(그쪽이 fail-closed로
   *   `false`를 쓴다). 여기서 번역하면 그 기본값이 두 곳이 되고, 나중에 한쪽만 고쳐지는 어긋남은
   *   응답 형상이 같아 **조용하다**. 승인이 필요 없을 때는 `undefined`가 와서 키가 아예 빠진다
   *   (`JSON.stringify`가 `undefined` 값을 생략한다).
   */
  bulkUpdateReleaseNoteAudiences: (
    ids: string[],
    audiences: string[],
    acknowledgeUnconstrained?: boolean,
  ) =>
    fetchApi<{ updated: number }>('/admin/release-notes/bulk-audiences', {
      method: 'POST',
      body: JSON.stringify({
        ids,
        audiences,
        acknowledgeUnconstrainedNamespaces: acknowledgeUnconstrained,
      }),
    }),

  // ============================================
  // 위험 대응 지식베이스 관리 (Hazard Knowledge)
  // ============================================
  getMeasures: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    industryType?: string;
    category?: string;
    subCategory?: string;
    source?: 'AI' | 'SIF' | 'EXTERNAL' | 'USER' | 'SYSTEM';
    organizationId?: string;
    sortBy?: 'usageCount' | 'confidence' | 'createdAt' | 'category';
    sortOrder?: 'asc' | 'desc';
    legalBasesStatus?: 'matched' | 'unmatched';
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.industryType) query.set('industryType', params.industryType);
    if (params?.category) query.set('category', params.category);
    if (params?.subCategory) query.set('subCategory', params.subCategory);
    if (params?.source) query.set('source', params.source);
    if (params?.organizationId) query.set('organizationId', params.organizationId);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    if (params?.legalBasesStatus) query.set('legalBasesStatus', params.legalBasesStatus);
    return fetchApi<{
      data: Array<{
        id: string;
        category: string;
        subCategory: string | null;
        detailCategory: string | null;
        hazardFactor: string | null;
        riskSituation: string | null;
        measures: Array<{
          id: string;
          content: string;
          category: string | null;
          confidence: number | null;
          usageCount: number;
          sortOrder: number;
        }>;
        source: 'AI' | 'SIF' | 'EXTERNAL' | 'USER' | 'SYSTEM';
        usageCount: number;
        confidence: number | null;
        regulations: Array<{
          id: string;
          regulationId: string;
          type: string;
          code: string | null;
          article: string | null;
          title: string | null;
          summary: string | null;
          url: string | null;
          relevance: number | null;
        }>;
        organizationId: string | null;
        sourceType: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/admin/knowledge?${query}`);
  },

  getMeasureStats: () =>
    fetchApi<{
      total: number;
      regulationMatched: number;
      bySource: {
        AI: number;
        SIF: number;
        EXTERNAL: number;
        USER: number;
        SYSTEM: number;
      };
      byCategory: Array<{
        category: string;
        count: number;
      }>;
      avgConfidence: number | null;
      totalUsage: number;
    }>('/admin/knowledge/stats'),

  getMeasureIndustryTypes: () =>
    fetchApi<string[]>('/admin/knowledge/industry-types'),

  getMeasureCategories: (industryType?: string) => {
    const query = new URLSearchParams();
    if (industryType) query.set('industryType', industryType);
    return fetchApi<string[]>(`/admin/knowledge/categories?${query}`);
  },

  getMeasureSubCategories: (category?: string) => {
    const query = new URLSearchParams();
    if (category) query.set('category', category);
    return fetchApi<string[]>(`/admin/knowledge/subcategories?${query}`);
  },

  getMeasure: (id: string) =>
    fetchApi<{
      id: string;
      category: string;
      subCategory: string | null;
      detailCategory: string | null;
      hazardFactor: string | null;
      riskSituation: string | null;
      measures: Array<{
        id: string;
        content: string;
        category: string | null;
        confidence: number | null;
        usageCount: number;
        sortOrder: number;
      }>;
      source: 'AI' | 'SIF' | 'EXTERNAL' | 'USER' | 'SYSTEM';
      usageCount: number;
      confidence: number | null;
      regulations: Array<{
        id: string;
        regulationId: string;
        type: string;
        code: string | null;
        article: string | null;
        title: string | null;
        summary: string | null;
        url: string | null;
        relevance: number | null;
      }>;
      organizationId: string | null;
      sourceType: string | null;
      createdAt: string;
      updatedAt: string;
    }>(`/admin/knowledge/${id}`),

  createMeasure: (data: {
    category: string;
    subCategory?: string | null;
    hazardFactor?: string | null;
    riskSituation?: string | null;
    measures: string[];
    source?: 'AI' | 'SIF' | 'EXTERNAL' | 'USER' | 'SYSTEM';
    organizationId?: string | null;
  }) =>
    fetchApi<{
      id: string;
      category: string;
      source: string;
    }>('/admin/knowledge', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMeasure: (
    id: string,
    data: {
      category?: string;
      subCategory?: string | null;
      hazardFactor?: string | null;
      riskSituation?: string | null;
      source?: 'AI' | 'SIF' | 'EXTERNAL' | 'USER' | 'SYSTEM';
      addMeasures?: string[];
      removeMeasureIds?: string[];
      updateMeasures?: Array<{
        id: string;
        content?: string;
        confidence?: number | null;
      }>;
    },
  ) =>
    fetchApi<{
      id: string;
      category: string;
      updatedAt: string;
    }>(`/admin/knowledge/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteMeasure: (id: string) =>
    fetchApi<void>(`/admin/knowledge/${id}`, {
      method: 'DELETE',
    }),

  deleteMeasures: (ids: string[]) =>
    fetchApi<{ deletedCount: number }>('/admin/knowledge', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    }),

  // ========================================
  // Regulation Management API
  // ========================================

  getRegulations: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    isActive?: boolean;
    sortBy?: 'type' | 'code' | 'createdAt' | 'effectiveDate';
    sortOrder?: 'asc' | 'desc';
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.type) query.set('type', params.type);
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    return fetchApi<{
      data: Array<{
        id: string;
        type: string;
        code: string | null;
        article: string | null;
        title: string | null;
        summary: string | null;
        /**
         * 조문에 딸린 항. 본문이 항에만 있는 조문(summary가 빈 문자열)이 실재하므로 계약에서
         * 빠뜨리면 소비처가 "없는 필드"로 알고 읽지 않는다 — 이번 사고의 원인이 그 누락이었다.
         */
        paragraphs?: Array<{ paragraphNo: string; content: string }>;
        url: string | null;
        effectiveDate: string | null;
        isActive: boolean;
        knowledgeLinkCount: number;
        findingLinkCount: number;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/admin/regulations?${query}`);
  },

  getRegulationStats: () =>
    fetchApi<{
      total: number;
      active: number;
      byType: Array<{ type: string; count: number }>;
      linkedToKnowledge: number;
      linkedToFindings: number;
      /**
       * 법령 버전 판정의 상주 신호(2026-10-26 신설 — 서버
       * `admin-regulations.service`의 `LawVersionSignals`가 정본).
       *
       * ⛔ **두 뜻이 섞여 있다** — `upcoming`은 판정이 **현행에서 내린** 것이고(0이 아닌 것이
       * 정상) `skipped*`는 판정을 **하지 않고 넘긴** 것이다(0이 정상). 화면에서 한 문장으로
       * 합치지 말 것. 단위도 다르다 — 앞 둘은 **행**, 뒤 둘은 **그룹**이라 **더할 수 없다**.
       *
       * ⚠️ **optional인 것은 의도다.** `cd.yml`의 `deploy-ec2`는 `needs: [all-tests-passed,
       * build-push]`이고 `deploy-admin`은 `needs: [all-tests-passed, build-admin, prepare]`라
       * **서로를 기다리지 않는다** ⇒ 두 배포가 병렬이고 **순서가 보장되지 않는다**. 그래서 새
       * 번들이 **이 칸이 없는 옛 서버 응답**을 읽는 창이 있다(그 반대도 마찬가지다).
       * ⛔ 더 긴 창이 하나 더 있다 — `deploy-admin`은 `prepare.outputs.admin == 'true'`도
       * 요구하므로 **server만 바꾼 배포에서는 admin이 아예 배포되지 않는다**. 그때 옛 번들이
       * 새 서버 응답을 **계속** 읽고, 캐시된 번들은 그 뒤로도 남는다.
       * ⇒ 소비처는 그 부재를 가드해야 한다 — 「서버가 항상 준다」로 읽고 `?.`를 떼지 말 것.
       */
      versionSignals?: {
        /** 시행 예정(미래 시행일) 시행본 **행** 수 — 판정이 현행에서 내린다 */
        upcoming: number;
        /** `law_id`가 없어 판정에서 제외된 **행** 수 */
        skippedNoGroupKey: number;
        /** 시행일이 없어 제외된 **행** 수 */
        skippedNoEnforcementDate: number;
        /** 한 `law_id`에 이름이 둘 이상이라 통째로 제외된 **그룹** 수 */
        skippedIncoherentGroups: number;
        /** 과거 시행본이 없어 현행을 고를 수 없는 **그룹** 수 */
        skippedNoCurrentCandidate: number;
      };
    }>('/admin/regulations/stats'),

  getRegulationTypes: () =>
    fetchApi<string[]>('/admin/regulations/types'),

  /**
   * 현행 법령 버전의 별표·서식(S1 · 2026-11-01). 식별자는 `lawId`(법제처 법령ID) 또는 `lawName`(법령명 완전일치) —
   * 법규 화면이 가진 것은 `getRegulationTypes`의 이름이라 화면은 `lawName`으로 친다. 계약 정본은 shared
   * `features/law-annex.ts`(`AdminLawAnnexesResponse` — `law: null`은 「현행 행 없음」이고 「별표 없음」과 다르다).
   * 읽기 전용이다 — 쓰기(수집)는 `syncRegulations` 하나(`regulationsReadOnly.test.ts`의 구조 축).
   */
  getRegulationAnnexes: (params: { lawId?: string; lawName?: string; kind?: string }) => {
    const query = new URLSearchParams();
    if (params.lawId) query.set('lawId', params.lawId);
    if (params.lawName) query.set('lawName', params.lawName);
    if (params.kind) query.set('kind', params.kind);
    return fetchApi<AdminLawAnnexesResponse>(`/admin/regulations/annexes?${query}`);
  },

  /**
   * 별표 복원의 「대응 안 된 기호」(대응표 밖이라 `?`로 남은 기호) + 복원이 돌았는지. 읽기 전용(저장된 가공 결과만).
   * 응답 형상 정본은 서버 `law/annex-grid.service.ts`의 `AnnexUnmappedSymbolsView`(이 타입은 그 사본 — shared에 두지 않았다).
   */
  getAnnexUnmappedSymbols: () =>
    fetchApi<AnnexUnmappedSymbolsView>('/admin/regulations/annexes/unmapped-symbols'),

  /**
   * 수집된 고시(법제처 행정규칙) 버전 목록(②-f · 2026-11-02). 읽기 전용 — 수집(`POST /admin/admin-rules/sync`)은
   * 법제처 외부 호출이라 이 화면에 버튼을 두지 않는다(사용자 확정 A3 ⓑ 수동 엔드포인트만).
   * 경로가 `/admin/regulations/…`가 아닌 이유는 서버 `AdminRulesController` docblock(`regulations/:id`에 먹힌다).
   */
  getAdminRules: () => fetchApi<AdminRuleListResponse>('/admin/admin-rules'),

  /** 고시 한 버전의 본문 · 조문 · 별표 · 부칙 */
  getAdminRule: (id: string) => fetchApi<AdminRuleDetailResponse>(`/admin/admin-rules/${encodeURIComponent(id)}`),

  // (제거됨) searchRegulations — `/admin/regulations/search`는 서버에 없는 라우트라 호출 시 404였다
  // (2026-08-05 실측). 조문 검색은 목록 엔드포인트의 search 파라미터, 즉 `getRegulations({ search })`.

  getRegulation: (id: string) =>
    fetchApi<{
      id: string;
      type: string;
      code: string | null;
      article: string | null;
      title: string | null;
      summary: string | null;
      fullText: string | null;
      url: string | null;
      effectiveDate: string | null;
      isActive: boolean;
      knowledgeLinkCount: number;
      findingLinkCount: number;
      createdAt: string;
      updatedAt: string;
    }>(`/admin/regulations/${id}`),

  // (제거됨 2026-10-25) createRegulation · updateRegulation · deleteRegulation ·
  // deleteRegulations · resetRegulations — 서버에 그 다섯 라우트가 **하나도 없어** 호출이 전부
  // 404였다(전수: `admin.controller`의 `regulations` 라우트는 `GET` 넷 + `POST regulations/sync`
  // 다섯뿐 · 같은 파일 다른 자원의 `@Post`/`@Patch`/`@Delete` 다수가 그 census의 대조군이다).
  //
  // 다시 넣지 말 것 — 라우트가 없는 것은 실수가 아니다. 법령 본문의 정본은 법제처(국가법령정보)
  // API이고 쓰기 수단은 아래 `syncRegulations` 하나이며, `isActive`의 소유자는 서버
  // `admin-regulations.service`의 `reevaluateLawVersionFlags`(수집 끝마다 전 그룹 재평가)다.
  // 손으로 쓰면 다음 수집이 덮는다. 경위는 `apps/admin/src/pages/Regulations.tsx` 머리 주석.

  syncRegulations: () =>
    fetchApi<{
      totalLaws: number;
      totalArticles: number;
      created: number;
      updated: number;
      errors: string[];
    }>('/admin/regulations/sync', { method: 'POST' }),

  /** 법규 동기화/임베딩 진행 상태 확인 */
  getEmbeddingStatus: () =>
    fetchApi<{
      inProgress: boolean;
      sync: boolean;
      regulation: boolean;
      regulationProgress?: { processed: number; total: number };
      knowledge: boolean;
      knowledgeProgress?: { processed: number; total: number };
      lawArticle: boolean;
      lawArticleProgress?: { processed: number; total: number };
      lawSubItem: boolean;
      lawSubItemProgress?: { processed: number; total: number };
    }>('/admin/knowledge/embedding-status'),

  /** LawArticle 임베딩 실행 */
  embedLawArticles: (forceAll?: boolean) =>
    fetchApi<{ total: number; embedded: number; skipped: number }>('/admin/knowledge/embed-law-articles', {
      method: 'POST',
      body: JSON.stringify({ forceAll: forceAll ?? false }),
    }),

  /** LawSubItem 임베딩 실행 */
  embedLawSubItems: (forceAll?: boolean) =>
    fetchApi<{ total: number; embedded: number; skipped: number }>('/admin/knowledge/embed-law-sub-items', {
      method: 'POST',
      body: JSON.stringify({ forceAll: forceAll ?? false }),
    }),

  // ========================================
  // Knowledge Base Import API
  // ========================================

  getImportFields: () =>
    fetchApi<Record<string, { label: string; required: boolean }>>('/admin/knowledge/import/fields'),

  analyzeImportFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<{
      fileName: string;
      sheets: Array<{
        name: string;
        columns: string[];
        rowCount: number;
        sampleData: Record<string, unknown>[];
      }>;
      suggestedMapping: Array<{
        sheetName: string;
        mapping: Record<string, string | null>;
        confidence: number;
      }>;
    }>('/admin/knowledge/import/analyze', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  analyzeImportFileWithAI: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<{
      fileName: string;
      sheets: Array<{
        name: string;
        columns: string[];
        rowCount: number;
        sampleData: Record<string, unknown>[];
        rawPreview?: Array<{ row: number; data: unknown[] }>;
      }>;
      suggestedMapping: Array<{
        sheetName: string;
        mapping: Record<string, string | number | null>;
        confidence: number;
      }>;
      aiRecommendation?: {
        recommendedSheet: string;
        fieldMapping: Record<string, { column: string | null; confidence: number }>;
        overallConfidence: number;
        reasoning: string;
      };
      comprehensiveAnalysis?: {
        documentType: string;
        importableSheets: Array<{
          sheetName: string;
          description: string;
          headerRows: number[];
          dataStartRow: number;
          estimatedDataRows: number;
          fieldMapping: Record<string, { columnIndex: number; headerText: string; confidence: number }>;
        }>;
        nonImportableSheets: Array<{ sheetName: string; reason: string }>;
        overallConfidence: number;
        reasoning: string;
      };
    }>('/admin/knowledge/import/analyze-ai', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  importSif: (file: File, sheets?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const url = sheets
      ? `/admin/knowledge/import/sif?sheets=${encodeURIComponent(sheets)}`
      : '/admin/knowledge/import/sif';
    return fetchApi<{ jobId: string }>(url, {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  importDynamic: (
    file: File | null,
    sourceType: string,
    mapping: Record<string, string | number | null>,
    sheetName?: string,
    skipDuplicates?: boolean,
    classificationColumns?: string[],
    headerRows?: number[],
    dataStartRow?: number,
    inferredIndustryType?: string,
    inferredCategory?: string,
    inferredSubCategory?: string,
    classificationSources?: { industryType?: 'AI' | 'USER' | 'DB'; category?: 'AI' | 'USER' | 'DB'; subCategory?: 'AI' | 'USER' | 'DB' },
    excludedRows?: number[],
    analyzeJobId?: string,
    analyzeFileName?: string,
  ) => {
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }
    formData.append('sourceType', sourceType);
    formData.append('mapping', JSON.stringify(mapping));
    if (sheetName) formData.append('sheetName', sheetName);
    if (skipDuplicates !== undefined) formData.append('skipDuplicates', String(skipDuplicates));
    if (classificationColumns && classificationColumns.length > 0) {
      formData.append('classificationColumns', JSON.stringify(classificationColumns));
    }
    if (headerRows && headerRows.length > 0) {
      formData.append('headerRows', JSON.stringify(headerRows));
    }
    if (dataStartRow !== undefined) {
      formData.append('dataStartRow', String(dataStartRow));
    }
    if (inferredIndustryType) {
      formData.append('inferredIndustryType', inferredIndustryType);
    }
    if (inferredCategory) {
      formData.append('inferredCategory', inferredCategory);
    }
    if (inferredSubCategory) {
      formData.append('inferredSubCategory', inferredSubCategory);
    }
    if (classificationSources) {
      formData.append('classificationSources', JSON.stringify(classificationSources));
    }
    if (excludedRows && excludedRows.length > 0) {
      formData.append('excludedRows', JSON.stringify(excludedRows));
    }
    if (analyzeJobId) {
      formData.append('analyzeJobId', analyzeJobId);
    }
    if (analyzeFileName) {
      formData.append('analyzeFileName', analyzeFileName);
    }
    return fetchApi<{ jobId: string }>('/admin/knowledge/import/execute', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  matchRegulations: (options?: { forceRematch?: boolean }) =>
    fetchApi<{ jobId: string }>('/admin/knowledge/match-regulations', {
      method: 'POST',
      body: JSON.stringify({ incrementalOnly: !options?.forceRematch }),
    }),

  getActiveMatchJob: () =>
    fetchApi<{
      jobId: string;
      status: 'processing' | 'completed' | 'failed';
      processed: number;
      total: number;
      matched: number;
      skipped: number;
    } | null>('/admin/knowledge/match-regulations/active'),

  getMatchRegulationsProgress: (jobId: string) =>
    fetchApi<{
      status: 'processing' | 'completed' | 'failed';
      processed: number;
      total: number;
      matched: number;
      skipped: number;
      avgSimilarity?: number;
      error?: string;
    }>(`/admin/knowledge/match-regulations/progress/${jobId}`),

  /** 법규 매칭 결과 소비 완료 (Redis 삭제, 새로고침 시 재표시 방지) */
  deleteMatchJob: (jobId: string) =>
    fetchApi<void>(`/admin/knowledge/match-regulations/${jobId}`, { method: 'DELETE' }),

  resetLegalBases: () =>
    fetchApi<{
      updated: number;
    }>('/admin/knowledge/reset-legal-bases', {
      method: 'POST',
    }),

  resetKnowledgeBase: (password: string) =>
    fetchApi<{
      deletedKnowledge: number;
      deletedMeasures: number;
      performedBy: string;
    }>('/admin/knowledge/reset', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),

  getActiveImport: () =>
    fetchApi<{
      jobId: string;
      status: 'processing' | 'completed' | 'failed';
      percent: number;
      currentSheet: string;
      processed: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      postProcess?: 'embedding' | null;
      embeddingProcessed?: number;
      embeddingTotal?: number;
    } | null>('/admin/knowledge/import/active'),

  getImportProgress: (jobId: string) =>
    fetchApi<{
      status: 'processing' | 'completed' | 'failed';
      percent: number;
      currentSheet: string;
      processed: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      result?: {
        success: boolean;
        totalProcessed: number;
        created: number;
        updated: number;
        skipped: number;
        errors: string[];
        details: Array<{
          sheet: string;
          processed: number;
          created: number;
          updated: number;
          skipped: number;
        }>;
      };
      error?: string;
      postProcess?: 'embedding' | null;
      embeddingProcessed?: number;
      embeddingTotal?: number;
    }>(`/admin/knowledge/import/progress/${jobId}`),

  /** Import 결과 소비 완료 (Redis 삭제, 새로고침 시 재표시 방지) */
  deleteImportJob: (jobId: string) =>
    fetchApi<void>(`/admin/knowledge/import/job/${jobId}`, { method: 'DELETE' }),

  /** 배치 Import 시작 (복수 시트를 서버에서 순차 처리) */
  startBatchImport: (config: {
    analyzeJobId: string;
    sourceType: string;
    skipDuplicates: boolean;
    sheets: Array<{
      fileName: string;
      sheetName: string;
      mapping: Record<string, string | number | null>;
      classificationColumns?: string[];
      headerRows?: number[];
      dataStartRow?: number;
      inferredIndustryType?: string;
      inferredCategory?: string;
      inferredSubCategory?: string;
      inferredDetailCategory?: string;
      classificationSources?: { industryType?: 'AI' | 'USER' | 'DB'; category?: 'AI' | 'USER' | 'DB'; subCategory?: 'AI' | 'USER' | 'DB'; detailCategory?: 'AI' | 'USER' | 'DB' };
      excludedRows?: number[];
      addToClassification?: boolean;
    }>;
  }) =>
    fetchApi<{ jobId: string }>('/admin/knowledge/import/batch', {
      method: 'POST',
      body: JSON.stringify(config),
    }),

  /** 활성 배치 Import 작업 조회 (새로고침 복구용) */
  getActiveBatchImport: () =>
    fetchApi<{
      jobId: string;
      status: 'processing' | 'completed' | 'failed';
      totalSheets: number;
      completedSheets: number;
      currentFileName: string;
      currentSheetName: string;
      currentSheetPercent: number;
      currentSheetProcessed: number;
      currentSheetTotal: number;
      totalCreated: number;
      totalUpdated: number;
      totalSkipped: number;
      postProcess?: 'embedding' | null;
      embeddingProcessed?: number;
      embeddingTotal?: number;
      sheetResults: Array<{ fileName: string; sheetName: string; created: number; updated: number; skipped: number; error?: string }>;
      result?: {
        success: boolean;
        totalProcessed: number;
        created: number;
        updated: number;
        skipped: number;
        errors: string[];
        details: Array<{ sheet: string; processed: number; created: number; updated: number; skipped: number }>;
      };
      error?: string;
    } | null>('/admin/knowledge/import/batch/active'),

  /** 배치 Import 진행률 조회 */
  getBatchImportProgress: (jobId: string) =>
    fetchApi<{
      status: 'processing' | 'completed' | 'failed';
      totalSheets: number;
      completedSheets: number;
      currentFileName: string;
      currentSheetName: string;
      currentSheetPercent: number;
      currentSheetProcessed: number;
      currentSheetTotal: number;
      totalCreated: number;
      totalUpdated: number;
      totalSkipped: number;
      postProcess?: 'embedding' | null;
      embeddingProcessed?: number;
      embeddingTotal?: number;
      sheetResults: Array<{ fileName: string; sheetName: string; created: number; updated: number; skipped: number; error?: string }>;
      result?: {
        success: boolean;
        totalProcessed: number;
        created: number;
        updated: number;
        skipped: number;
        errors: string[];
        details: Array<{ sheet: string; processed: number; created: number; updated: number; skipped: number }>;
      };
      error?: string;
    }>(`/admin/knowledge/import/batch/progress/${jobId}`),

  /** 배치 Import 결과 소비 완료 (Redis 삭제, 새로고침 시 재표시 방지) */
  deleteBatchImportJob: (jobId: string) =>
    fetchApi<void>(`/admin/knowledge/import/batch/${jobId}`, { method: 'DELETE' }),

  /** 배치 분석 시작 (복수 파일) */
  analyzeBatch: (files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return fetchApi<{ jobId: string }>('/admin/knowledge/import/analyze-batch', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  /** 활성 배치 분석 작업 조회 */
  getActiveAnalyze: () =>
    fetchApi<{
      jobId: string;
      status: 'processing' | 'completed' | 'failed';
      total: number;
      completed: number;
      activeFiles: string[];
      results: Record<string, any>;
      error?: string;
    } | null>('/admin/knowledge/import/analyze/active'),

  /** 배치 분석 진행률 조회 */
  getAnalyzeProgress: (jobId: string) =>
    fetchApi<{
      status: 'processing' | 'completed' | 'failed';
      total: number;
      completed: number;
      activeFiles: string[];
      results: Record<string, any>;
      error?: string;
    }>(`/admin/knowledge/import/analyze/progress/${jobId}`),

  /** 분석 결과 소비 완료 (중복 복구 방지) */
  deleteAnalyzeJob: (jobId: string) =>
    fetchApi<void>(`/admin/knowledge/import/analyze/${jobId}`, { method: 'DELETE' }),

  // ==========================================
  // 사고사례 (AccidentCase)
  // ==========================================

  getAccidentCases: (params?: {
    page?: number;
    limit?: number;
    industryType?: string;
    category?: string;
    subCategory?: string;
    causingAgent?: string;
    accidentType?: string;
    source?: string;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.industryType) query.set('industryType', params.industryType);
    if (params?.category) query.set('category', params.category);
    if (params?.subCategory) query.set('subCategory', params.subCategory);
    if (params?.causingAgent) query.set('causingAgent', params.causingAgent);
    if (params?.accidentType) query.set('accidentType', params.accidentType);
    if (params?.source) query.set('source', params.source);
    if (params?.search) query.set('search', params.search);
    return fetchApi<{
      items: AccidentCase[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/admin/accident-cases?${query}`);
  },

  getAccidentCase: (id: string) =>
    fetchApi<AccidentCase>(`/admin/accident-cases/${id}`),

  getAccidentCaseCoverage: () =>
    fetchApi<{
      total: number;
      byIndustry: Array<{ industryType: string; count: number }>;
      coverage: CoverageStatItem[];
      gaps: CoverageStatItem[];
    }>('/admin/accident-cases/coverage'),

  getLinkCandidates: (params: {
    industryType: string;
    category: string;
    subCategory?: string;
    detailCategory: string;
  }) => {
    const query = new URLSearchParams({
      industryType: params.industryType,
      category: params.category,
      detailCategory: params.detailCategory,
    });
    if (params.subCategory) query.set('subCategory', params.subCategory);
    return fetchApi<LinkCandidateGroup[]>(
      `/admin/accident-cases/link-candidates?${query}`,
    );
  },

  saveLinks: (params: {
    industryType: string;
    category: string;
    subCategory?: string;
    detailCategory: string;
    accidentCaseIds: string[];
  }) =>
    fetchApi<{ saved: number }>('/admin/accident-cases/links', {
      method: 'PUT',
      body: JSON.stringify(params),
    }),

  getAccidentCaseOptions: (filters?: { industryType?: string; category?: string }) => {
    const params = new URLSearchParams();
    if (filters?.industryType) params.set('industryType', filters.industryType);
    if (filters?.category) params.set('category', filters.category);
    const qs = params.toString();
    return fetchApi<{
      industries: string[];
      categories: string[];
      subCategories: string[];
      causingAgents: string[];
      accidentTypes: string[];
    }>(`/admin/accident-cases/options${qs ? `?${qs}` : ''}`);
  },

  createAccidentCase: (data: AccidentCaseInput) =>
    fetchApi<AccidentCase>('/admin/accident-cases', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAccidentCase: (id: string, data: Partial<AccidentCaseInput>) =>
    fetchApi<AccidentCase>(`/admin/accident-cases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteAccidentCase: (id: string) =>
    fetchApi<null>(`/admin/accident-cases/${id}`, {
      method: 'DELETE',
    }),

  resetAccidentCases: (password: string) =>
    fetchApi<{ deletedCount: number }>('/admin/accident-cases/reset', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),

  // 사고사례 SIF 가져오기 (비동기 - jobId 반환)
  importSifAccidentCases: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<{ jobId: string }>('/admin/accident-cases/import-sif', {
      method: 'POST',
      body: formData,
      headers: {}, // FormData는 Content-Type을 자동 설정하도록 비워둠
    });
  },

  // 사고사례 활성 Import 조회
  getActiveAccidentCaseImport: () =>
    fetchApi<{
      jobId: string;
      status: 'processing' | 'completed' | 'failed';
      percent: number;
      currentSheet: string;
      processed: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
    } | null>('/admin/accident-cases/import/active'),

  // 사고사례 Import 진행률 조회
  getAccidentCaseImportProgress: (jobId: string) =>
    fetchApi<{
      status: 'processing' | 'completed' | 'failed';
      percent: number;
      currentSheet: string;
      processed: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      result?: {
        success: boolean;
        totalProcessed: number;
        created: number;
        updated: number;
        skipped: number;
        errors: string[];
        details?: Array<{
          sheet: string;
          processed: number;
          created: number;
          updated: number;
          skipped: number;
        }>;
      };
      error?: string;
    }>(`/admin/accident-cases/import/progress/${jobId}`),

  // ==========================================
  // 공종-작업 분류 (WorkClassification)
  // ==========================================

  getWorkClassifications: (params?: {
    page?: number;
    limit?: number;
    industryType?: string;
    category?: string;
    subCategory?: string;
    source?: 'SIF' | 'AI' | 'USER';
    isOfficial?: boolean;
    search?: string;
    sortBy?: 'category' | 'subCategory' | 'usageCount' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.industryType) query.set('industryType', params.industryType);
    if (params?.category) query.set('category', params.category);
    if (params?.subCategory) query.set('subCategory', params.subCategory);
    if (params?.source) query.set('source', params.source);
    if (params?.isOfficial !== undefined) query.set('isOfficial', String(params.isOfficial));
    if (params?.search) query.set('search', params.search);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    return fetchApi<WorkClassification[]>(`/admin/work-classifications?${query}`);
  },

  getWorkClassificationStats: () =>
    fetchApi<{
      total: number;
      official: number;
      verified: number;
      bySource: Record<string, number>;
      byIndustryType: Array<{ industryType: string; count: number }>;
      topCategories: Array<{ category: string; count: number }>;
    }>('/admin/work-classifications/stats'),

  getWorkClassificationCategories: (industryType?: string) => {
    const query = new URLSearchParams();
    if (industryType) query.set('industryType', industryType);
    return fetchApi<{
      industryTypes: string[];
      categories: string[];
    }>(`/admin/work-classifications/categories?${query}`);
  },

  /** 드롭다운 옵션 조회 (Cascading dropdown용) */
  getWorkClassificationDropdownOptions: (filters?: { industryType?: string; category?: string; subCategory?: string }) => {
    const query = new URLSearchParams();
    if (filters?.industryType) query.set('industryType', filters.industryType);
    if (filters?.category) query.set('category', filters.category);
    if (filters?.subCategory) query.set('subCategory', filters.subCategory);
    return fetchApi<{
      industryTypes: string[];
      categories: { value: string; industryType: string }[];
      subCategories: { value: string; category: string; industryType: string }[];
      detailCategories: { value: string; subCategory: string; category: string; industryType: string }[];
    }>(`/admin/work-classifications/dropdown-options?${query}`);
  },

  lookupWorkClassification: (industryType: string, subCategory: string) =>
    fetchApi<WorkClassification[]>(
      `/admin/work-classifications/lookup?industryType=${encodeURIComponent(industryType)}&subCategory=${encodeURIComponent(subCategory)}`
    ),

  getWorkClassification: (id: string) =>
    fetchApi<WorkClassification>(`/admin/work-classifications/${id}`),

  createWorkClassification: (data: WorkClassificationInput) =>
    fetchApi<WorkClassification>('/admin/work-classifications', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateWorkClassification: (id: string, data: Partial<WorkClassificationInput>) =>
    fetchApi<WorkClassification>(`/admin/work-classifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteWorkClassification: (id: string) =>
    fetchApi<null>(`/admin/work-classifications/${id}`, {
      method: 'DELETE',
    }),

  deleteWorkClassifications: (ids: string[]) =>
    fetchApi<{ deletedCount: number }>('/admin/work-classifications', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    }),

  importSifWorkClassifications: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<{ jobId: string }>('/admin/work-classifications/import-sif', {
      method: 'POST',
      body: formData,
    });
  },

  getActiveWcSifImport: () =>
    fetchApi<WcSifImportProgress | null>('/admin/work-classifications/import-sif/active'),

  getWcSifImportProgress: (jobId: string) =>
    fetchApi<WcSifImportProgress>(`/admin/work-classifications/import-sif/progress/${jobId}`),

  resetWorkClassifications: (password: string) =>
    fetchApi<{ deletedCount: number }>('/admin/work-classifications/reset', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),

  // ==========================================
  // 업종별 공종 분류 (IndustryWorkCategory)
  // ==========================================

  getIndustryWorkCategories: (params?: {
    /** 미지정 = 공통 코어(모든 업종 폴백) 축 */
    industryId?: string;
    /** 업종 축 조회 시 공통 코어도 함께(읽기 전용 표시) */
    includeCommon?: boolean;
    isActive?: boolean;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.industryId) query.set('industryId', params.industryId);
    if (params?.includeCommon) query.set('includeCommon', 'true');
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
    if (params?.search) query.set('search', params.search);
    return fetchApi<IndustryWorkCategoryNode[]>(
      `/admin/industry-work-categories?${query}`
    );
  },

  createIndustryWorkCategory: (data: {
    industryId?: string | null;
    parentId?: string | null;
    name: string;
    order?: number;
  }) =>
    fetchApi<IndustryWorkCategoryNode>('/admin/industry-work-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** 이름·정렬·활성 여부만 — 축(업종/계층) 이동은 서버가 받지 않는다 */
  updateIndustryWorkCategory: (
    id: string,
    data: { name?: string; order?: number; isActive?: boolean }
  ) =>
    fetchApi<IndustryWorkCategoryNode>(`/admin/industry-work-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // ==========================================
  // AI 위험분석 현황
  // ==========================================

  getAiAnalysisStats: () =>
    fetchApi<AiAnalysisStats>('/admin/knowledge/ai-analyses/stats'),

  getAiAnalysisFilterOptions: (params?: {
    industryType?: string;
    hkCategory?: string;
    workName?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.industryType) query.set('industryType', params.industryType);
    if (params?.hkCategory) query.set('hkCategory', params.hkCategory);
    if (params?.workName) query.set('workName', params.workName);
    return fetchApi<{
      industryTypes: string[];
      hkCategories: { value: string }[];
      workNames: { value: string }[];
      detailWorks: { value: string }[];
    }>(`/admin/knowledge/ai-analyses/filter-options?${query}`);
  },

  getAiAnalysisList: (params: {
    page?: number;
    limit?: number;
    search?: string;
    matchStatus?: string;
    // ⚠️ `severity?: string`는 2026-10-07에 **삭제했다** — 서버가 그 쿼리를 읽지 않고
    // (`knowledge.controller.ts`에 히트 0건), 읽게 만들어도 걸러지지 않는다: 어휘가 조직 config마다
    // 다르고(dev 한 목록에 `상/중/하`·`높음/보통/낮음`이 섞인다) 심각도는 **파생값**이라 SQL에서
    // 거를 수 없어 `total`·쪽수가 거짓이 된다. 다시 더하지 말 것 — 사유와 복원 조건은
    // `AiMatching.tsx`의 심각도 select 자리 주석이 정본이다.
    industryType?: string;
    hkCategory?: string;
    workName?: string;
    detailWork?: string;
    /**
     * 한 사진의 분석 이력으로 좁히는 **URL 스코프**(P4-f ⓒ-3의 받는 쪽) — ML 소스 화면의
     * 「분석 이력」 링크가 `?photoId=`로 들어온다. UI 필터가 아니라 들어온 스코프다.
     *
     * ⚠️ **0건이 정상인 입력이다** — `ai_analysis_logs`는 사진 전체를 덮지 않으므로(dev 실측
     * 273장 중 195장만 로그를 가진다) 빈 결과를 「조회 실패」로 읽지 말 것. 화면은 그 경우
     * 「이 사진에는 분석 로그가 없다」를 명시한다.
     */
    photoId?: string;
  }) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.photoId) query.set('photoId', params.photoId);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.matchStatus) query.set('matchStatus', params.matchStatus);
    if (params.industryType) query.set('industryType', params.industryType);
    if (params.hkCategory) query.set('hkCategory', params.hkCategory);
    if (params.workName) query.set('workName', params.workName);
    if (params.detailWork) query.set('detailWork', params.detailWork);
    return fetchApi<AiAnalysisListResponse>(`/admin/knowledge/ai-analyses?${query}`);
  },

  getAiAnalysisDetail: (id: string) =>
    fetchApi<AiAnalysisDetail>(`/admin/knowledge/ai-analyses/${id}`),

  unmatchAnalysisLog: (id: string) =>
    fetchApi<null>(`/admin/knowledge/ai-analyses/${id}/unmatch`, { method: 'DELETE' }),

  getUnmatchedPatterns: () =>
    fetchApi<UnmatchedPattern[]>('/admin/knowledge/ai-analyses/unmatched-patterns'),

  createKnowledgeFromPattern: (data: {
    industryType?: string | null;
    category: string;
    subCategory?: string | null;
    detailCategory?: string | null;
    hazardFactor: string;
    riskSituation: string;
    measures: string[];
    logIds: string[];
    addToWorkClassification?: boolean;
  }) =>
    fetchApi<{ knowledgeId: string; linkedCount: number }>(
      '/admin/knowledge/ai-analyses/unmatched-patterns/create-knowledge',
      { method: 'POST', body: JSON.stringify(data) },
    ),

  suggestClassification: (data: { workName: string; detailWork?: string | null }) =>
    fetchApi<{ suggestions: WorkClassificationSuggestion[] }>(
      '/admin/knowledge/suggest-classification',
      { method: 'POST', body: JSON.stringify(data) },
    ),

  // ==========================================
  // Feature Flags
  // ==========================================

  getFeatureFlags: (params?: {
    status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
    search?: string;
    tag?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    if (params?.tag) query.set('tag', params.tag);
    return fetchApi<FeatureFlag[]>(`/admin/feature-flags?${query}`);
  },

  getFeatureFlag: (id: string) =>
    fetchApi<FeatureFlag>(`/admin/feature-flags/${id}`),

  createFeatureFlag: (data: {
    key: string;
    name: string;
    description?: string;
    type?: 'BOOLEAN' | 'MULTIVARIATE';
    tags?: string[];
  }) =>
    fetchApi<FeatureFlag>('/admin/feature-flags', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFeatureFlag: (id: string, data: {
    name?: string;
    description?: string;
    status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
    tags?: string[];
  }) =>
    fetchApi<FeatureFlag>(`/admin/feature-flags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteFeatureFlag: (id: string) =>
    fetchApi<void>(`/admin/feature-flags/${id}`, { method: 'DELETE' }),

  restoreFeatureFlag: (id: string) =>
    fetchApi<FeatureFlag>(`/admin/feature-flags/${id}/restore`, { method: 'POST' }),

  toggleFeatureFlag: (id: string) =>
    fetchApi<FeatureFlag>(`/admin/feature-flags/${id}/toggle`, { method: 'POST' }),

  updateFeatureFlagRollout: (id: string, percentage: number) =>
    fetchApi<FeatureFlag>(`/admin/feature-flags/${id}/rollout`, {
      method: 'PATCH',
      body: JSON.stringify({ percentage }),
    }),

  addFeatureFlagOverride: (id: string, data: {
    targetType: 'USER' | 'ORGANIZATION';
    targetId: string;
    value: unknown;
  }) =>
    fetchApi<FeatureFlagOverride>(`/admin/feature-flags/${id}/overrides`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeFeatureFlagOverride: (id: string, overrideId: string) =>
    fetchApi<void>(`/admin/feature-flags/${id}/overrides/${overrideId}`, {
      method: 'DELETE',
    }),

  updateFeatureFlagOverrideValue: (flagId: string, overrideId: string, value: boolean) =>
    fetchApi<void>(`/admin/feature-flags/${flagId}/overrides/${overrideId}`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  getFeatureFlagAuditLog: (id: string) =>
    fetchApi<FeatureFlagAuditEntry[]>(`/admin/feature-flags/${id}/audit-log`),

  searchFeatureFlagTargets: (type: 'USER' | 'ORGANIZATION', q: string) =>
    fetchApi<FeatureFlagSearchTarget[]>(
      `/admin/feature-flags/search-targets?type=${type}&q=${encodeURIComponent(q)}`,
    ),

  // 현재 사용자의 피처 플래그 평가 결과
  getMyFeatureFlags: () =>
    fetchApi<Record<string, unknown>>('/feature-flags/me'),

  // 플래그-그룹 오버라이드
  addFeatureFlagGroupOverride: (flagId: string, data: { groupId: string; value: unknown }) =>
    fetchApi<void>(`/admin/feature-flags/${flagId}/group-overrides`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeFeatureFlagGroupOverride: (flagId: string, groupId: string) =>
    fetchApi<void>(`/admin/feature-flags/${flagId}/group-overrides/${groupId}`, {
      method: 'DELETE',
    }),

  updateFeatureFlagGroupOverrideValue: (flagId: string, groupId: string, value: boolean) =>
    fetchApi<void>(`/admin/feature-flags/${flagId}/group-overrides/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  // ==========================================
  // Feature Flag Groups
  // ==========================================

  getFeatureFlagGroups: (search?: string) => {
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    return fetchApi<FeatureFlagGroup[]>(`/admin/feature-flag-groups?${query}`);
  },

  getFeatureFlagGroup: (id: string) =>
    fetchApi<FeatureFlagGroupDetail>(`/admin/feature-flag-groups/${id}`),

  createFeatureFlagGroup: (data: { name: string; description?: string }) =>
    fetchApi<FeatureFlagGroup>('/admin/feature-flag-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFeatureFlagGroup: (id: string, data: { name?: string; description?: string }) =>
    fetchApi<FeatureFlagGroup>(`/admin/feature-flag-groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteFeatureFlagGroup: (id: string) =>
    fetchApi<void>(`/admin/feature-flag-groups/${id}`, { method: 'DELETE' }),

  addFeatureFlagGroupMembers: (groupId: string, userIds: string[]) =>
    fetchApi<void>(`/admin/feature-flag-groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    }),

  removeFeatureFlagGroupMember: (groupId: string, userId: string) =>
    fetchApi<void>(`/admin/feature-flag-groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
    }),

  // 관리자 메시지 발송
  sendAdminMessage: (dto: {
    targetType: 'all' | 'organization' | 'users';
    targetId?: string;
    userIds?: string[];
    title: string;
    message: string;
    priority: 'normal' | 'important';
  }) =>
    fetchApi<{ sentCount: number; targetDescription: string }>(
      '/admin/notifications/send',
      {
        method: 'POST',
        body: JSON.stringify(dto),
      },
    ),

  // ==========================================
  // External Services
  // ==========================================

  getExternalServicesStatus: () =>
    fetchApi<{ sms: { provider: string; connected: boolean; balance: { sms: number; lms: number; mms: number } | null; error?: string; checkedAt: string } }>('/admin/external-services/status'),

  getSmsDailyStats: (startDate: string, endDate: string) =>
    fetchApi<SmsDailyStatsItem[]>(
      `/admin/external-services/sms/daily-stats?startDate=${startDate}&endDate=${endDate}`,
    ),

  collectSmsDailyStats: (date: string) =>
    fetchApi<SmsDailyStatsItem>(
      '/admin/external-services/sms/daily-stats/collect',
      { method: 'POST', body: JSON.stringify({ date }) },
    ),

  getSmsSendLogs: (date: string) =>
    fetchApi<{ logs: SmsSendLogItem[] }>(
      `/admin/external-services/sms/send-logs?date=${date}`,
    ),

  getSmsPrefixes: () =>
    fetchApi<SmsPrefix[]>('/admin/external-services/sms/prefixes'),

  createSmsPrefix: (data: { prefix: string; label?: string; serviceKey?: string }) =>
    fetchApi<SmsPrefix>('/admin/external-services/sms/prefixes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSmsPrefix: (id: string, data: { label?: string; isActive?: boolean; serviceKey?: string }) =>
    fetchApi<SmsPrefix>(`/admin/external-services/sms/prefixes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteSmsPrefix: (id: string) =>
    fetchApi<void>(`/admin/external-services/sms/prefixes/${id}`, {
      method: 'DELETE',
    }),

  setDefaultSmsPrefix: (id: string) =>
    fetchApi<SmsPrefix>(`/admin/external-services/sms/prefixes/${id}/set-default`, {
      method: 'POST',
    }),

  // ==========================================
  // 위험 카테고리 관리
  // ==========================================

  getHazardCategories: (companyId: string) =>
    fetchApi<HazardCategory[]>(`/admin/companies/${companyId}/hazard-categories`),

  createHazardCategory: (companyId: string, label: string) =>
    fetchApi<{ id: string; code: string; label: string; isSystem: boolean }>(
      `/admin/companies/${companyId}/hazard-categories`,
      { method: 'POST', body: JSON.stringify({ label }) },
    ),

  deleteHazardCategory: (companyId: string, categoryId: string) =>
    fetchApi<{ deleted: boolean }>(
      `/admin/companies/${companyId}/hazard-categories/${categoryId}`,
      { method: 'DELETE' },
    ),

  // ==========================================
  // 사용자 피드백 관리 (UserFeedback)
  // ==========================================

  getUserFeedbackList: (params?: {
    type?: string;
    status?: string;
    /**
     * 조직 필터. 서버 DTO는 `QueryUserFeedbackDto.companyId`다 —
     * 2026-10-10까지 이 자리가 `organizationId`였고 쿼리 키도 그렇게 보냈다.
     * ⚠️ 그 키는 무시되는 것이 아니라 **400**이다(`forbidNonWhitelisted`). 오늘 호출부가 0건이라
     * 잠복했을 뿐이고, 처음 배선하는 사람이 그 400을 만났을 것이다.
     */
    companyId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.status) query.set('status', params.status);
    if (params?.companyId) query.set('companyId', params.companyId);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    return fetchApi<UserFeedback[]>(`/admin/user-feedback?${query}`);
  },

  getUserFeedbackDetail: (id: string) =>
    fetchApi<UserFeedback>(`/admin/user-feedback/${id}`),

  updateUserFeedback: (
    id: string,
    data: {
      type?: UserFeedbackType;
      status?: UserFeedbackStatus;
      adminNote?: string;
      priority?: UserFeedbackPriority;
    },
  ) =>
    fetchApi<UserFeedback>(`/admin/user-feedback/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getUserFeedbackAttachmentUrl: (feedbackId: string, attachmentId: string): string => {
    const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    return `${API_HOST}/api/v1/admin/user-feedback/${feedbackId}/attachments/${attachmentId}`;
  },

  getNotificationSettings: () =>
    fetchApi<NotificationSetting[]>('/admin/user-feedback/notification-settings'),

  createNotificationSetting: (data: {
    channel: FeedbackNotificationChannel;
    target: string;
    label?: string;
    isActive?: boolean;
    feedbackTypes?: UserFeedbackType[];
  }) =>
    fetchApi<NotificationSetting>('/admin/user-feedback/notification-settings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateNotificationSetting: (
    id: string,
    data: {
      target?: string;
      label?: string;
      isActive?: boolean;
      feedbackTypes?: UserFeedbackType[];
    },
  ) =>
    fetchApi<NotificationSetting>(`/admin/user-feedback/notification-settings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteNotificationSetting: (id: string) =>
    fetchApi<void>(`/admin/user-feedback/notification-settings/${id}`, {
      method: 'DELETE',
    }),

  // ===== ApprovalLineMeta (현장 결재선 override 관리) =====
  // 축 값(`templateKey` 응답 필드)은 Composer 템플릿 key다. cascade는 현장 override → 템플릿 두 층 — 마지막 층
  // (구조·한도·활성 여부)은 템플릿 `approvalLineMeta`(Composer 에디터 양식 탭에서 저작)다.
  // system 층·조직 override 층의 테이블도 엔드포인트도 없다 — 라우트·응답 필드는 `approval-line-meta/:templateKey`.

  getSiteApprovalLineMetas: (siteId: string) =>
    fetchApi<{
      siteOverrides: SiteApprovalLineMetaData[];
    }>(`/sites/${siteId}/approval-line-meta`),

  // PATCH body는 `{ lines, reason }`뿐이다 — 한도(`maxAdditionalLines`)·활성(`hasApprovalLinePolicy`)은 양식 단독
  // 권위라 현장 행 컬럼째 2026-09-16(approver-profile-retirement E4 + R1) DROP했고, 서버 DTO가 whitelist라
  // 남겨 보내면 400이다. GET 응답의 두 필드는 서버가 양식 값으로 채워 준다(형상 유지).
  updateSiteApprovalLineMeta: (
    siteId: string,
    templateKey: string,
    body: {
      lines: ApprovalLineMetaRow[];
      reason: string;
    },
  ) =>
    fetchApi<null>(`/sites/${siteId}/approval-line-meta/${templateKey}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  getApprovalLineMetaHistory: () =>
    fetchApi<
      Array<{
        id: string;
        templateKey: string;
        scope: string;
        scopeId: string | null;
        changedBy: string;
        reason: string;
        snapshotData: unknown;
        changedAt: string;
      }>
    >('/admin/approval-line-meta/history'),

  // ===== Report Composer 템플릿 CRUD (3c-2) =====
  // 서버 CRUD는 admin 전용(JwtAuthGuard + SuperAdminGuard). 응답은 {success,data,error}.
  // data는 StoredReportTemplate(ReportTemplate + id/enabled/createdAt/updatedAt) 형태이며
  // JSON 직렬화로 Date는 ISO 문자열로 내려온다.

  listReportTemplates: () =>
    fetchApi<StoredReportTemplate[]>('/admin/report-composer/templates'),

  getReportTemplate: (key: string) =>
    fetchApi<StoredReportTemplate>(
      `/admin/report-composer/templates/${encodeURIComponent(key)}`,
    ),

  createReportTemplate: (body: CreateReportTemplateBody) =>
    fetchApi<StoredReportTemplate>('/admin/report-composer/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateReportTemplate: (key: string, body: UpdateReportTemplateBody) =>
    fetchApi<StoredReportTemplate>(
      `/admin/report-composer/templates/${encodeURIComponent(key)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    ),

  deleteReportTemplate: (key: string) =>
    fetchApi<null>(`/admin/report-composer/templates/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  // 삭제 «전» 경고(L24 · US-0723) — 이 양식을 현재 쓰는 법정 의무. 비차단: 삭제는 이 목록과 무관하게 되고
  // 그 연결은 서버가 해제한다(`ReportComposerService.deleteTemplate` docblock).
  getReportTemplateDeleteImpact: (key: string) =>
    fetchApi<{
      key: string;
      obligations: Array<{
        obligationId: string;
        key: string;
        title: string;
        role: 'PRIMARY' | 'ALTERNATIVE' | 'PARTIAL' | 'SUMMARY';
        isActive: boolean;
      }>;
    }>(`/admin/report-composer/templates/${encodeURIComponent(key)}/delete-impact`),

  // 템플릿 복제 — 콘텐츠는 서버가 원본에서 복사, body는 새 key/name만. 복제본은 항상 비공개(enabled=false).
  duplicateReportTemplate: (sourceKey: string, body: { key: string; name: string }) =>
    fetchApi<StoredReportTemplate>(
      `/admin/report-composer/templates/${encodeURIComponent(sourceKey)}/duplicate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  // ===== Report Composer 양식 분류(category) CRUD (2026-09-20 결정) =====
  // 분류는 전역(admin 관리). 양식 ↔ 분류 배정은 updateReportTemplate body의 categoryId가 맡는다.

  listReportTemplateCategories: () =>
    fetchApi<ReportTemplateCategory[]>('/admin/report-composer/template-categories'),

  /** 생성 — 이름만. 같은 이름이면 409(서버 메시지 그대로 토스트). 정렬은 목록 끝. */
  createReportTemplateCategory: (body: { name: string }) =>
    fetchApi<ReportTemplateCategory>('/admin/report-composer/template-categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** 이름·정렬 부분 갱신 — 전달한 필드만 바뀐다. */
  updateReportTemplateCategory: (id: string, body: { name?: string; sortOrder?: number }) =>
    fetchApi<ReportTemplateCategory>(
      `/admin/report-composer/template-categories/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    ),

  /** 삭제 — 소속 양식은 미분류로(서버 SetNull). 응답 movedToUncategorized = 이동한 양식 수. */
  deleteReportTemplateCategory: (id: string) =>
    fetchApi<{ id: string; movedToUncategorized: number }>(
      `/admin/report-composer/template-categories/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  // ===== 양식 업로드 → 템플릿 초안 (form-import Job, 2026-07-27) =====
  // 업로드는 jobId만 즉시 반환하고 분석은 서버에서 비동기 진행 → useJobPolling으로 진행률 폴링.

  /**
   * 양식(JPEG/PNG/PDF/엑셀, 20MB) 업로드 → 분석 Job 시작. multipart 필드명은 서버
   * FileInterceptor와 동일한 'file'. sheetNames는 엑셀 시트 지정(빈 배열이면 서버가
   * 내용이 있는 첫 시트 자동 선택 — 비엑셀 파일에는 무의미). 여러 개면 서버가 **순차**로
   * 변환하며 시트마다 초안 1개가 생긴다.
   *
   * 배열은 같은 이름의 필드를 반복해 싣는다(multipart 관례) — 서버 DTO가 1개짜리 문자열을
   * 배열로 승격한다.
   */
  startFormImport: (file: File, sheetNames?: string[]) => {
    const formData = new FormData();
    formData.append('file', file);
    for (const name of sheetNames ?? []) {
      if (name) formData.append('sheetNames', name);
    }
    return fetchApi<{ jobId: string }>('/admin/report-composer/form-import', {
      method: 'POST',
      body: formData,
      headers: {}, // FormData는 Content-Type(boundary)을 자동 설정하도록 비워둠
    });
  },

  /**
   * 양식 엑셀의 시트 이름 목록 — 시트 선택 UI 노출 여부 판단용. 서버가 격자를 만들지 않고
   * 워크북 디렉터리만 읽으므로 시트가 많은 파일도 통과한다(데이터셋 격자 파싱 API를 재사용하던
   * 종전 경로는 그 상한에 걸려 선택 UI가 사라졌다). 이름만 오므로 "내용 있는 시트" 판정은 없다.
   */
  getFormImportSheetNames: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<{ sheetNames: string[] }>(
      '/admin/report-composer/form-import/sheet-names',
      {
        method: 'POST',
        body: formData,
        headers: {}, // FormData는 Content-Type(boundary)을 자동 설정하도록 비워둠
      },
    );
  },

  /** 활성 Job 조회(새로고침 복구용) — processing 우선, 없으면 completed/failed fallback. */
  getActiveFormImportJob: () =>
    fetchApi<ActiveFormImportJob | null>('/admin/report-composer/form-import/active'),

  /** Job 진행률 조회 — 폴링 tick. */
  getFormImportProgress: (jobId: string) =>
    fetchApi<FormImportProgress | null>(
      `/admin/report-composer/form-import/${encodeURIComponent(jobId)}`,
    ),

  /** 완료/실패 결과 소비 후 삭제 — 새로고침 시 소비된 결과 재표시 방지. */
  deleteFormImportJob: (jobId: string) =>
    fetchApi<null>(`/admin/report-composer/form-import/${encodeURIComponent(jobId)}`, {
      method: 'DELETE',
    }),

  /**
   * 진행 중 Job 취소 요청 — DELETE(결과 소비 후 삭제)와 **다른 경로**다. DELETE는 저장소만
   * 비워 폴링이 결과를 잃는 반면 서버는 끝까지 돌아 초안을 계속 만든다.
   *
   * 즉시 끊기지 않는다 — 현재 시트가 끝난 뒤 반영되고, 그때까지 만들어진 초안은 남는다.
   */
  cancelFormImportJob: (jobId: string) =>
    fetchApi<{ accepted: boolean; status: FormImportProgress['status'] }>(
      `/admin/report-composer/form-import/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST' },
    ),

  // ===== 데이터셋 엑셀 행 가져오기 (2026-08-09) =====

  /**
   * 데이터셋용 엑셀 파싱 — 시트별 원시 셀 격자를 **동기**로 받는다(AI 없음 → Job 폴링 불필요).
   * 헤더 행 판정·칼럼 매핑·값 변환은 전부 클라이언트(datasetExcelImport.ts) 몫이라 서버는
   * 격자만 준다. multipart 필드명은 서버 FileInterceptor와 동일한 'file'. xlsx만·5MB.
   */
  parseDatasetXlsx: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<{ sheets: DatasetXlsxSheet[] }>(
      '/admin/report-composer/dataset-import/parse-xlsx',
      {
        method: 'POST',
        body: formData,
        headers: {}, // FormData는 Content-Type(boundary)을 자동 설정하도록 비워둠
      },
    );
  },

  /**
   * 데이터 연결 후보 제안(P2) — 현재 편집 상태의 수동 입력 필드 목록을 보내면 활성 AI가
   * 카탈로그 매칭 후보를 반환한다(동기 1콜, 실패 시 503). 임포트 초안뿐 아니라 손저작
   * 템플릿에도 쓰는 범용 도구.
   */
  suggestBindings: (fields: BindingSuggestField[]) =>
    fetchApi<{ suggestions: BindingSuggestion[] }>('/admin/report-composer/suggest-bindings', {
      method: 'POST',
      body: JSON.stringify({ fields }),
    }),

  // 카탈로그(노드 바인딩 편집용) — JwtAuthGuard + SuperAdminGuard. CatalogEntry[] 반환.
  getReportComposerCatalog: () =>
    fetchApi<CatalogEntry[]>('/admin/report-composer/catalog'),

  // 카탈로그 행 즉시 등록(작성 탭 캔버스 라이브 모드 submitAction 모달) — keyed upsert writer
  // 화이트리스트 전용. 성공 시 등록/갱신된 행(카탈로그 rows 형식, id 포함) 반환.
  submitReportComposerCatalogRow: (
    catalogKey: string,
    body: { companyId: string; siteId: string; values: Record<string, string> },
  ) =>
    fetchApi<Record<string, unknown>>(
      `/admin/report-composer/catalog/${encodeURIComponent(catalogKey)}/rows`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // 미리보기 실데이터 resolve(3-preview) — super_admin 전용. body {template, reportId}.
  // data 카탈로그를 reportId 컨텍스트로 실해석한 content + 미해석 바인딩(gaps) 반환.
  previewReportComposer: (template: ReportTemplate, reportId: string) =>
    fetchApi<ReportComposerPreviewResponse>('/admin/report-composer/preview', {
      method: 'POST',
      body: JSON.stringify({ template, reportId }),
    }),

  // 점검에 연결된 보고서 목록(미리보기 「보고서 연결」 선택지) — super_admin 전용. 최신순.
  // 결정 기록: docs/decisions/2026-09-04-composer-preview-linked-report-and-preset-default.md
  getReportComposerPreviewContextReports: (inspectionId: string) =>
    fetchApi<
      Array<{
        id: string;
        title: string;
        status: string;
        createdAt: string;
        // 서버 계약(report-composer.service preview-context-reports)은 null 가능 — 차수 미보유
        // 보고서. 소비처 previewContextReportLabel이 그 null을 이미 분기한다(2026-09-14 정렬).
        sequenceNumber: number | null;
      }>
    >(
      `/admin/report-composer/preview-context-reports?inspectionId=${encodeURIComponent(inspectionId)}`,
    ),

  // 미리보기 실데이터 resolve(엔티티 컨텍스트) — super_admin 전용. body {template, context}.
  // reportId 단일 대신 조직/현장/점검/계정을 직접 지정해 그 실데이터로 content + gaps 반환.
  // 결정 기록: docs/decisions/2026-06-22-composer-preview-context-entity-cascade.md
  // linkedReportId(선택, 2026-09-04 명시적 보고서 연결)는 **body 최상위** 축이다(context 안이
  // 아니다 — 서버 PreviewByContextDto 계약). 지정 시 그 보고서의 저장 데이터로 렌더하며 서버가
  // previewSimulation을 전면 무시한다(클라 strip과 이중 방어).
  previewReportComposerByContext: (
    template: ReportTemplate,
    context: ReportComposerPreviewContext,
    linkedReportId?: string,
  ) =>
    fetchApi<ReportComposerPreviewResponse>(
      '/admin/report-composer/preview-by-context',
      {
        method: 'POST',
        body: JSON.stringify({
          template,
          context,
          ...(linkedReportId ? { linkedReportId } : {}),
        }),
      },
    ),

  // 작성 탭 캔버스 초기값(prefill) resolve — super_admin 전용. body {template, context}.
  // 작성자(web) getComposerPrefill과 동일 서버 로직(resolvePrefill) 재사용 — input prefillFrom +
  // persistScope 저장소 + 변수 last-used를 컨텍스트로 해석한 { [bindingKey]: value } 맵.
  // 작성 탭 "실데이터 반영" 상태에서 defaultValue 위에 덮어 "작성 시작 시점"을 WYSIWYG로 표시.
  prefillReportComposerByContext: (
    template: ReportTemplate,
    context: ReportComposerPreviewContext,
  ) =>
    fetchApi<Record<string, unknown>>(
      '/admin/report-composer/prefill-by-context',
      {
        method: 'POST',
        body: JSON.stringify({ template, context }),
      },
    ),

  // 미리보기 분할 계획 조회(그룹 목록) — super_admin 전용. body {template, context}.
  //
  // ⚠️ **부수효과가 있다** — provider classifier가 도는데 TBM은 TbmMeta upsert + AI 정제 캐시
  // getOrCreate를 수반한다. 읽기처럼 생겼지만 운영 데이터에 쓰므로 **폴링·자동 재조회 금지**:
  // 사용자의 명시적 조작(그룹 칸 열기)에서만 1회 부르고 결과는 그 점검 선택 동안 재사용한다.
  //
  // 저장 전 편집본(template)을 그대로 실어, 저작자가 방금 고친 policy.split이 반영된다.
  // 분할 정책이 없거나 대상 행이 0건이면 plan: null — 화면은 그 경우 그룹 칸을 띄우지 않는다.
  splitPlanForReportComposerByContext: (
    template: ReportTemplate,
    context: ReportComposerPreviewContext,
  ) =>
    fetchApi<{ plan: SplitPlan | null }>(
      '/admin/report-composer/split-plan-by-context',
      {
        method: 'POST',
        body: JSON.stringify({ template, context }),
      },
    ),

  // 작성 탭 캔버스 AI 개선 의견 후보 resolve(sortableSelect 실데이터 미리보기) — super_admin 전용.
  // 작성자(web) 경로와 같은 생성기 재사용(캐시 우선·없으면 생성) — 캔버스가 최종 작성 화면과 동일한
  // 후보를 보여야 그 결과로 디자인할 수 있다(WYSIWYG, 2026-07-16). 감지된 파트만 요청해 불필요한
  // AI 생성을 막는다. 테넌트 격리는 서버(assertContextOwnership)가 검증.
  improvementCandidatesForReportComposer: (
    inspectionId: string,
    companyId: string,
    parts?: ImprovementCandidatePart[],
  ) =>
    fetchApi<ImprovementCandidatesResponse>(
      '/admin/report-composer/improvement-candidates-by-context',
      {
        method: 'POST',
        body: JSON.stringify({ inspectionId, companyId, parts }),
      },
    ),

  // COMPOSED 생성 dry-run 시뮬레이션 — super_admin 전용. 실생성과 같은 서버 합성 함수를 실행하되
  // DB write·알림 등 부수효과 0. 노출 정책·중복·멤버십 게이트 skip, 데이터 정합(현장-회사 소속,
  // 점검-현장 일치)만 검증. 결정 기록: docs/decisions/2026-07-26-composer-simulation.md
  simulateReportComposerCompose: (body: SimulateComposeBody) =>
    fetchApi<SimulateComposeResult>('/admin/report-composer/simulate-compose', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ===== 위젯 프리셋 v1 (2026-07-17 결정) — 라이브러리 CRUD. super_admin 전용. =====
  // 격리: companyId 지정 시 "플랫폼(null) + 그 업체", 미지정 시 플랫폼만(admin 라이브러리 기본 뷰).
  listWidgetPresets: (companyId?: string) => {
    const query = companyId ? `?${new URLSearchParams({ companyId })}` : '';
    return fetchApi<WidgetPreset[]>(`/admin/report-composer/presets${query}`);
  },

  // 단건 + 버전 목록(version desc, 스냅샷 포함). 비가시(타 업체/soft delete)는 서버가 404.
  getWidgetPreset: (id: string) =>
    fetchApi<WidgetPresetWithVersions>(
      `/admin/report-composer/presets/${encodeURIComponent(id)}`,
    ),

  // 생성 = v1 발행(preset + version 1 원자 생성). changeNote 필수, ownerCompanyId 미지정=플랫폼.
  createWidgetPreset: (body: CreateWidgetPresetBody) =>
    fetchApi<WidgetPresetWithVersions>('/admin/report-composer/presets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // 새 버전 발행 — currentVersion+1, 구버전 스냅샷은 행으로 보존. { preset, version } 반환.
  publishWidgetPresetVersion: (id: string, body: PublishWidgetPresetVersionBody) =>
    fetchApi<{ preset: WidgetPreset; version: WidgetPresetVersion }>(
      `/admin/report-composer/presets/${encodeURIComponent(id)}/versions`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // 이름/설명 수정 — 버전 스냅샷 불변, 메타만 갱신. 갱신된 프리셋 행 반환.
  updateWidgetPresetMeta: (id: string, body: UpdateWidgetPresetMetaBody) =>
    fetchApi<WidgetPreset>(
      `/admin/report-composer/presets/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  // soft delete — 배치 인스턴스는 복사본이라 무영향(출처 메타만 detach 표시). soft-deleted 행 반환.
  deleteWidgetPreset: (id: string) =>
    fetchApi<WidgetPreset>(
      `/admin/report-composer/presets/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
};

// ===== Admin Companies (사업장 관리) =====

export type AdminCompanyVerificationStatus =
  | 'NOT_APPLICABLE'
  | 'STATUS_VERIFIED'
  | 'FULLY_VERIFIED';
export type AdminCompanyListSortBy = 'createdAt' | 'name' | 'ntsVerifiedAt';
export type AdminCompanyListSortOrder = 'asc' | 'desc';

export interface AdminCompanyListParams {
  page?: number;
  limit?: number;
  q?: string;
  verificationStatus?: AdminCompanyVerificationStatus;
  /** 가입(구독) 유무 필터 — 정본 클레임(claimedAt)과 다른 축 */
  hasSubscription?: 'true' | 'false';
  sortBy?: AdminCompanyListSortBy;
  sortOrder?: AdminCompanyListSortOrder;
}

export interface AdminCompanyListItem {
  id: string;
  name: string;
  /** 동명 회사 구분용 — 개인 워크스페이스는 등록번호·대표자가 비어 이 셋이 실질 구분자 */
  category: OrganizationCategory;
  ownerName: string | null;
  reportCount: number;
  registrationNumber: string | null;
  ceoName: string | null;
  openingDate: string | null;
  verificationStatus: AdminCompanyVerificationStatus;
  ntsVerifiedAt: string | null;
  ntsBusinessStatus: string | null;
  /** 가입(구독) 유무 — 정본 클레임 여부가 아니다 */
  hasSubscription: boolean;
  organizationCount: number;
  siteCount: number;
  createdAt: string;
  updatedAt: string;
  createdByOrgId?: string | null;
  createdByOrgName?: string | null;
}

export interface AdminCompanyListResponse {
  items: AdminCompanyListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminCompanyDetail {
  id: string;
  name: string;
  registrationNumber: string | null;
  ceoName: string | null;
  openingDate: string | null;
  industry: string | null;
  licenseNumber: string | null;
  contact: string | null;
  email: string | null;
  address: string | null;
  zonecode: string | null;
  addressDetail: string | null;
  verificationStatus: AdminCompanyVerificationStatus;
  ntsVerifiedAt: string | null;
  ntsBusinessStatus: string | null;
  ntsCanonicalName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCompanyOrganizationSummary {
  id: string;
  name: string;
  createdAt: string;
}

export interface AdminCompanySiteSummary {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  address: string | null;
  createdAt: string;
}

export interface AdminCompanyDetailResponse {
  company: AdminCompanyDetail;
  organization: AdminCompanyOrganizationSummary | null;
  sites: AdminCompanySiteSummary[];
}

export interface AdminCompanyReverifyResponse {
  ok: true;
  canonicalName: string;
  verifiedAt: string;
  verificationStatus: AdminCompanyVerificationStatus;
}

/**
 * 병합 보류 건이 왜 흡수되지 않았는지 — **현재** 붙어 있는 실체.
 * 감사 로그에 사유가 없어 서버가 지금 상태에서 다시 센 값이다.
 */
export interface AdminMergeBacklogAttachments {
  members: number;
  siteContacts: number;
  inspections: number;
  reports: number;
  groups: number;
  hasSubscription: boolean;
}

export interface AdminMergeBacklogCompany {
  id: string;
  name: string;
  trustLevel: CompanyTrustLevel;
}

export interface AdminMergeBacklogItem {
  auditLogId: string;
  /** 병합이 이 회사를 건너뛴 시각 = 클레임 확정 시각 */
  occurredAt: string;
  registrationNumber: string | null;
  canonical: AdminMergeBacklogCompany | null;
  skipped: AdminMergeBacklogCompany & {
    createdByCompanyId: string | null;
    createdByCompanyName: string | null;
    attachments: AdminMergeBacklogAttachments;
  };
}

export interface AdminMergeBacklogResponse {
  items: AdminMergeBacklogItem[];
  total: number;
  page: number;
  limit: number;
  /** 클레임 로그 조회 상한(서버 W8) 도달 여부 — 오래된 로그 일부가 판정에서 빠졌을 수 있음 */
  truncated: boolean;
}

/**
 * 테넌트 통합(흡수) 요청 — 운영자 중재 대상 (P4-B).
 *
 * 서버 정본: `CompanyMergeRequestService.getAdminBacklog` — ACCEPTED(수락됐으나 엔진 미완:
 * 재시도 대상) + REJECTED/EXPIRED(사람이 결론을 내야 하는 건)만 내려온다.
 */
export type AdminMergeRequestStatus =
  | 'PENDING'
  | 'ACCEPTED'
  | 'EXECUTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

export interface AdminMergeRequestCompany {
  id: string;
  name: string;
  registrationNumber: string | null;
}

export interface AdminMergeRequestItem {
  id: string;
  token: string;
  status: AdminMergeRequestStatus;
  canonicalCompanyId: string;
  targetCompanyId: string;
  rejectReason: string | null;
  expiresAt: string;
  resolvedAt: string | null;
  executedAt: string | null;
  createdAt: string;
  updatedAt: string;
  canonicalCompany: AdminMergeRequestCompany | null;
  targetCompany: AdminMergeRequestCompany | null;
}

export interface AdminMergeRequestsResponse {
  items: AdminMergeRequestItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminMergeRequestExecuteResponse {
  id: string;
  status: AdminMergeRequestStatus;
  executionSummary: Record<string, unknown> | null;
}

export const adminCompaniesApi = {
  list: (params?: AdminCompanyListParams) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.q) query.set('q', params.q);
    if (params?.verificationStatus) query.set('verificationStatus', params.verificationStatus);
    if (params?.hasSubscription) query.set('hasSubscription', params.hasSubscription);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    const qs = query.toString();
    return fetchApi<AdminCompanyListResponse>(`/admin/companies${qs ? `?${qs}` : ''}`);
  },

  detail: (id: string) =>
    fetchApi<AdminCompanyDetailResponse>(`/admin/companies/${id}`),

  reverify: (id: string) =>
    fetchApi<AdminCompanyReverifyResponse>(`/admin/companies/${id}/reverify`, {
      method: 'POST',
    }),

  mergeBacklog: (params?: { page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchApi<AdminMergeBacklogResponse>(
      `/admin/companies/merge-backlog${qs ? `?${qs}` : ''}`,
    );
  },

  mergeRequests: (params?: { page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchApi<AdminMergeRequestsResponse>(
      `/admin/companies/merge-requests${qs ? `?${qs}` : ''}`,
    );
  },

  /** 흡수 강제 실행 — ACCEPTED 재시도 또는 REJECTED/EXPIRED를 중재 결론으로 진행. */
  executeMergeRequest: (requestId: string) =>
    fetchApi<AdminMergeRequestExecuteResponse>(
      `/admin/companies/merge-requests/${requestId}/execute`,
      { method: 'POST' },
    ),

  /** 기각 — 중재 결론이 "통합하지 않음"일 때 백로그에서 내린다(CANCELLED 전이). */
  dismissMergeRequest: (requestId: string) =>
    fetchApi<{ id: string; status: AdminMergeRequestStatus }>(
      `/admin/companies/merge-requests/${requestId}/dismiss`,
      { method: 'POST' },
    ),
};

// 사고사례 타입 정의
export interface AccidentCase {
  id: string;
  industryType: string;
  category: string;
  subCategory: string | null;
  detailCategory: string | null;
  causingAgent: string | null;
  accidentType: string | null;
  hazardFactor: string | null;
  title: string | null;
  summary: string;
  injuryType: 'DEATH' | 'SERIOUS' | 'MINOR' | 'NEAR_MISS' | null;
  bodyPart: string | null;
  accidentDate: string | null;
  contextTags: string[];
  sceneDescription: string | null;
  source: 'SIF' | 'KOSHA_API' | 'KOSHA_CRAWL' | 'MOEL' | 'MANUAL';
  sourceId: string | null;
  sourceUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccidentCaseInput {
  industryType: string;
  category: string;
  subCategory?: string;
  detailCategory?: string;
  causingAgent?: string;
  accidentType?: string;
  hazardFactor?: string;
  title?: string;
  summary: string;
  injuryType?: 'DEATH' | 'SERIOUS' | 'MINOR' | 'NEAR_MISS';
  bodyPart?: string;
  accidentDate?: string;
  contextTags?: string[];
  sceneDescription?: string;
  sourceUrl?: string;
}

export interface CoverageStatItem {
  industryType: string;
  category: string;
  subCategory: string | null;
  detailCategory: string | null;
  causingAgent: string | null;
  accidentType: string | null;
  count: number;
  linkedCount?: number;
}

export interface LinkCandidateGroup {
  detailCategory: string;
  cases: {
    id: string;
    summary: string;
    causingAgent: string | null;
    accidentType: string | null;
    isLinked: boolean;
  }[];
}

// 공종-작업 분류 SIF Import 진행 상태
export interface WcSifImportProgress {
  jobId?: string;
  status: 'processing' | 'completed' | 'failed';
  percent: number;
  phase: 'importing' | 'embedding';
  processed: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  embeddingProcessed: number;
  embeddingTotal: number;
  result?: { created: number; updated: number; skipped: number; total: number };
  error?: string;
}

// 공종-작업 분류 타입 정의
export interface WorkClassification {
  id: string;
  industryType: string;
  category: string;
  subCategory: string;
  detailCategory: string | null;
  source: 'SIF' | 'AI' | 'USER';
  isOfficial: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkClassificationInput {
  industryType: string;
  category: string;
  subCategory: string;
  detailCategory?: string;
  source?: 'SIF' | 'AI' | 'USER';
  isOfficial?: boolean;
}

/**
 * 업종별 공종 분류 한 행. `parentId === null`이면 공종(대분류), 값이 있으면 작업명(중분류).
 * 목록 응답은 대분류에 `children`을 접어서 내려준다.
 */
export interface IndustryWorkCategoryNode {
  id: string;
  industryId: string | null;
  parentId: string | null;
  name: string;
  /** 'seed' = 초안 v0, 'promoted' = 운영에서 등록·승격 */
  source: string;
  order: number;
  isActive: boolean;
  /** 공통 코어(전 업종 폴백) 행 — 업종 축 화면에서는 읽기 전용으로 보여준다 */
  isCommon: boolean;
  createdAt: string;
  updatedAt: string;
  children?: IndustryWorkCategoryNode[];
}

// AI 위험분석 타입 정의
export interface AiAnalysisStats {
  totalFindings: number;
  withKnowledge: number;
  withoutKnowledge: number;
  bySeverity: Record<string, number>;
  topWorkNames: Array<{ workName: string; count: number }>;
}

export interface AiAnalysisListItem {
  id: string;
  category: string;
  subCategory: string | null;
  description: string;
  /**
   * 위험도 — **`ai_analysis_logs`에는 이 칼럼이 없다.** 서버가 그 로그의 발견사항
   * (`inspection_findings.aiLikelihoodScore`·`aiSeverityScore`)에서 shared `scoreToRiskLevel`로
   * 파생해 내린다.
   *
   * ⚠️ **점수가 없는 행은 `null`이다**(dev 486건 중 8건) — 「낮음」으로 떨어뜨리면 위험도를
   * 과소평가한 **거짓 사실**이 되므로 화면은 「미산출」로 가른다(`severityLabel`).
   * 2026-10-07까지 이 키를 `string`으로 선언하고 서버는 아예 내리지 않아, 배지가 빈 회색으로
   * 뜨는 것을 tsc가 원리적으로 못 봤다.
   */
  severity: string | null;
  aiConfidence: number | null;
  createdAt: string;
  knowledgeMatched: boolean;
  matchScore: number | null;
  regulationLinks: Array<{ type: string; article?: string; summary?: string }> | null;
  inferredWorkName: string | null;
  inferredDetailWork: string | null;
  /**
   * ⚠️ `ai_analysis_logs`에는 `deleted_at`이 **없다** — 이 로그의 수명은 사진·점검이 정한다.
   * 그래서 지워진 사진의 분석 로그가 목록에 그대로 남는다(P4-f ⓒ-1이 그것을 **보이게** 한다).
   */
  photo: { url: string; deletedAt: string | null } | null;
  inspection: {
    id: string;
    deletedAt: string | null;
    /**
     * 수검업체(구 `site.company`) — **AUDITED link가 없는 현장에서는 `null`**이다(자기 조직이
     * 직접 운영하는 현장이 흔하다. `site-company-link.util.ts` `siteAuditedCompany` JSDoc).
     * 폴백 없이 쓰지 말 것 — `siteLabel()`이 그 자리를 소유한다.
     *
     * ⚠️ 목록은 서버 경로가 **둘**이다(Prisma `getAnalysisList` / HK·작업명 필터 raw SQL
     * `getAnalysisListWithHkFilter`). 2026-10-07까지 raw 경로만 `organization`을 내려 세 평탄화
     * 자리 중 하나가 갈려 있었고, 타입 선언이 **그 하나에만** 맞아 5개월간 숨었다 —
     * 서버를 `company`로 통일했다. 이 키를 고칠 때는 그 세 자리를 함께 열 것
     * (`matcher.service.ts` 목록 Prisma·목록 raw·상세).
     */
    site: {
      name: string;
      company: { name: string } | null;
    } | null;
  };
}

export interface AiAnalysisListResponse {
  items: AiAnalysisListItem[];
  total: number;
}

export interface AiAnalysisDetail {
  id: string;
  category: string;
  subCategory: string | null;
  description: string;
  /** `AiAnalysisListItem.severity`와 같은 파생·같은 `null` 의미(「미산출」). */
  severity: string | null;
  aiConfidence: number | null;
  aiProvider: string | null;
  aiModel: string | null;
  aiAnalysisDetail: {
    knowledgeMatchMethod?: 'rag' | 'embedding';
    ragReason?: string;
    ragConfidence?: number;
    [key: string]: unknown;
  } | null;
  knowledgeMatched: boolean;
  matchedKnowledgeId: string | null;
  matchScore: number | null;
  ragReason: string | null;
  knowledgeMeasures: string[] | null;
  regulationLinks: Array<{ type: string; article?: string; summary?: string }> | null;
  inferredWorkName: string | null;
  inferredDetailWork: string | null;
  createdAt: string;
  /** 수명 축 — `AiAnalysisListItem.photo`와 같은 이유로 `deletedAt`을 싣는다. */
  photo: { url: string; deletedAt: string | null } | null;
  inspection: {
    id: string;
    deletedAt: string | null;
    site: {
      name: string;
      /**
       * 수검업체(구 `site.company`) — 서버 `getAnalysisDetail`이 `siteAuditedCompany`로 평탄화해
       * 내린다. **AUDITED link가 없는 현장에서는 `null`**이므로 폴백 없이 쓰면 안 된다.
       *
       * ⚠️ 2026-05-29 `5c2ade732`(H-4 G1)에서 서버가 `organization` → `company`로 바꿀 때 이
       * 선언이 동반되지 않아, 상세 모달이 `site.organization.name`을 읽어 TypeError로 죽고 있었다.
       * 타입이 거짓이면 tsc가 원리적으로 못 잡는다 — 이 키를 고칠 때는 서버 반환문을 열어 볼 것.
       */
      company: { name: string } | null;
    } | null;
  };
  matchedKnowledge?: {
    id: string;
    category: string;
    subCategory: string | null;
    hazardFactor: string | null;
    source: string;
    knowledgeMeasures: Array<{ id: string; content: string; sortOrder: number }>;
  } | null;
}

// 미매칭 패턴 타입 (임베딩 클러스터링 기반)
export interface UnmatchedPattern {
  clusterId: string;
  category: string;
  subCategory: string | null;
  representativeDescription: string;
  inferredWorkName: string | null;
  inferredDetailWork: string | null;
  industryType: string | null;
  count: number;
  aiMeasures: string[];
  findings: Array<{
    id: string;
    description: string;
    ragReason: string | null;
    createdAt: string;
    photoUrl: string | null;
    photoThumbnailUrl: string | null;
    /**
     * 수명 축 — 이 승격 후보가 **어떤 출처에서 나왔는지**를 화면에 적기 위한 값이다(P4-f ⓒ 파생).
     *
     * ⚠️ **경고가 아니라 사실 표시다.** 결정 기록 `2026-10-07-photo-lifecycle-scenario-spec.md`(ⓖ)가
     * 「모집단을 미리 좁히면 나중에 못 넓힌다 — 학습 단계에서 고른다」로 정했고 **삭제된 사진·맥락도
     * ML 자산**이다. 그래서 이 값으로 승격을 막거나(`disabled`) 기본 제외하거나 경고를 띄우지 않는다.
     *
     * ⚠️ **세 값은 각각 다른 사실이다 — 하나로 뭉개면 라벨이 거짓이 된다.**
     * - `photoDeletedAt` — 사진이 지워졌다 → 「사진 삭제됨」
     * - `inspectionDeletedAt` — 점검이 지워졌다 → 「(점검 삭제)」
     * - `findingDeletedAt` — 발견사항이 지워졌다 → 「(발견사항 삭제)」.
     *   사진 soft delete가 그 발견사항에도 함께 찍히므로(`inspection-photo.service.ts`)
     *   대개 `photoDeletedAt`과 같이 오지만, **발견사항만 지워진 경우**가 있어
     *   「사진 삭제됨」으로 옮겨 쓰면 거짓이 된다.
     *
     * ⚠️ 「지금 새고 있다」가 아니다 — 삭제 모집단이 아직 0이고 사진 삭제 배선이 미배포라
     * **배포 직후부터 참이 되는** 축이다.
     */
    photoDeletedAt: string | null;
    inspectionDeletedAt: string | null;
    findingDeletedAt: string | null;
  }>;
}

export interface WorkClassificationSuggestion {
  id: string;
  industryType: string;
  category: string;
  subCategory: string;
  detailCategory: string | null;
  similarity: number;
}

// Feature Flag 타입 정의
export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  type: 'BOOLEAN' | 'MULTIVARIATE';
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  enabled: boolean;
  defaultValue: unknown;
  rolloutPercentage: number;
  tags: string[];
  createdById: string;
  createdAt: string;
  updatedAt: string;
  variants: FeatureFlagVariant[];
  overrides: FeatureFlagOverride[];
  groupOverrides: FeatureFlagGroupOverride[];
}

export interface FeatureFlagVariant {
  id: string;
  key: string;
  value: unknown;
  weight: number;
}

export interface FeatureFlagOverride {
  id: string;
  targetType: 'USER' | 'ORGANIZATION';
  targetId: string;
  targetName: string | null;
  targetEmail: string | null;
  value: unknown;
  groupLinkId: string | null;
  createdAt: string;
}

export interface FeatureFlagGroupOverride {
  id: string;
  groupId: string;
  groupName: string | null;
  memberCount: number;
  value: unknown;
  createdAt: string;
}

export interface FeatureFlagAuditEntry {
  id: string;
  action: string;
  oldValue?: Record<string, unknown> | null;
  newValue?: Record<string, unknown> | null;
  context?: { actorName?: string; actorEmail?: string } | null;
  superAdminId?: string | null;
  createdAt: string;
}

export interface FeatureFlagSearchTarget {
  id: string;
  name: string;
  email: string | null;
  label: 'superAdmin' | 'user' | 'organization';
}

export interface FeatureFlagGroup {
  id: string;
  name: string;
  description: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  flagLinkCount: number;
}

export interface FeatureFlagGroupMember {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  createdAt: string;
}

export interface FeatureFlagGroupFlagLink {
  id: string;
  flagId: string;
  flagKey: string;
  flagName: string;
  value: unknown;
  createdAt: string;
}

export interface FeatureFlagGroupDetail extends FeatureFlagGroup {
  members: FeatureFlagGroupMember[];
  flagLinks: FeatureFlagGroupFlagLink[];
}

// ============================================
// 사용자 피드백 (UserFeedback) 타입
// ============================================

// ⚠️ 유형·첨부종류는 **shared가 정본**이다(`types/user-feedback.ts` — Prisma enum 미러).
// 종전엔 여기 손union으로 적어 두고 화면 문구는 또 따로 적었고, 그래서 web과 admin이 같은 값을
// 다른 이름으로 불렀다(2026-10-12 리뷰 ①). `WithdrawalReasonType`·`SiteAssignmentType` 선례와 같이
// 재export만 한다 — 값을 여기 다시 열거하면 드리프트가 되살아난다.
// (`export … from`이 아니라 재export다 — 이 파일 안에서도 쓰므로 위 import로 로컬 바인딩이 필요하다.)
export type { UserFeedbackType, UserFeedbackAttachmentType };
export type UserFeedbackStatus = 'PENDING' | 'REVIEWING' | 'ACCEPTED' | 'REJECTED' | 'RESOLVED';
export type UserFeedbackPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type FeedbackNotificationChannel = 'SLACK' | 'EMAIL';

/**
 * 피드백 첨부 — **서버가 실제로 내려주는 칸만** 선언한다.
 *
 * ⚠️ 2026-10-23까지 이 선언이 `storagePath: string`과 `url: string | null`을 들고 있었는데
 * **서버는 둘 다 주지 않는다**: `storagePath`는 응답에서 제외됐고(그 축의 탐지기는
 * `user-feedback.service.spec.ts`의 `not.toContain('storagePath')` 단언이다) `url`은 종전
 * `mapFeedbackWithUrls`가 더하던 칸인데 **그 조립이 사라졌다**(서버 `user-feedback.service.ts`
 * 머리 주석이 과거형으로 적는다). 둘을 읽는 admin 자리는 **0건**이었으므로 런타임 피해는 없었고,
 * 고친 이유는 **타입이 거짓이면 다음 사람이 그 칸을 쓰고 tsc가 통과하는데 런타임에 `undefined`**가
 * 되기 때문이다(= 거짓 안심). 칸을 되살리려면 **서버 select를 먼저 넓혀라.**
 *
 * ⛔ **「첨부 응답이 최소다」로 읽지 말 것** — 서버는 `mimeType`·`size`·`sortOrder`·`fileId`·
 * `feedbackId`·`createdAt`도 준다(민감하지 않다). 그 축을 좁히는 것은 별건이고 가치가 낮다.
 */
export interface UserFeedbackAttachment {
  id: string;
  feedbackId: string;
  type: UserFeedbackAttachmentType;
  filename: string;
  mimeType: string;
  size: number;
  sortOrder: number;
  fileId: string | null;
  createdAt: string;
}

export interface UserFeedback {
  id: string;
  feedbackNumber: number;
  type: UserFeedbackType;
  title: string;
  description: string | null;
  diagnostics: Record<string, unknown> | null;
  pageUrl: string | null;
  status: UserFeedbackStatus;
  priority: UserFeedbackPriority;
  adminNote: string | null;
  resolvedAt: string | null;
  resolvedById: string | null;
  userId: string;
  /**
   * 제출 당시 소속(결정 2026-10-10). 스키마는 `company_id` **NOT NULL**이다.
   *
   * ⚠️ 2026-10-10까지 이 둘은 `organizationId`·`organization`이었다 — 서버는 Phase H-8에서
   * `company`로 바꿨는데 admin 타입·렌더가 따라오지 않아 **조직이 영영 표시되지 않았다**
   * (상세는 `{feedback.organization && …}` 조건부 렌더라 조용히 사라지고, 목록은 항상 `-`).
   * tsc는 못 잡는다 — 선언과 실제 응답을 대조하는 것이 없다.
   */
  companyId: string;
  createdAt: string;
  updatedAt: string;
  user: { deletedAt?: string | null; member: { name: string } | null; auths: Array<{ provider: string; providerId: string }> };
  company: { name: string } | null;
  resolvedBy: { deletedAt?: string | null; member: { name: string } | null } | null;
  attachments: UserFeedbackAttachment[];
}

export interface NotificationSetting {
  id: string;
  channel: FeedbackNotificationChannel;
  target: string;
  label: string | null;
  isActive: boolean;
  feedbackTypes: UserFeedbackType[];
  createdAt: string;
  updatedAt: string;
}

export interface UserFeedbackListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// API 에러 추출 헬퍼
export const extractApiError = (error: unknown): { code: string; message: string } => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiResponse<unknown> | undefined;
    if (data?.error) {
      return data.error;
    }
    return {
      code: 'NETWORK_ERROR',
      message: error.message || '네트워크 오류가 발생했습니다.',
    };
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: '알 수 없는 오류가 발생했습니다.',
  };
};

export default api;
